// this is a component which handles both the Lexa Speech and the voice recognition of the app
// no other component should directly control either of these two things
// transcipts can be parsed elsewhere, but listening should only be turned on and off from here
// in addition this is the only component which should start or end speech
// any request from another component to start listening, stop listening, speak,
// or stop speaking, should go through App's instance of this component


import React from 'react';
import LexAudioRecorder, { globalContext, prepareMicForPlayback, prepareMicForRecord, setPreCheckStep, isAudioProcessingEnabled } from '../../audiocapture/recorder';
import SpeechRecognition from './SpeechRecognition/SpeechRecognition';
import LexaSpeech from './LexaSpeech';
import {
  saveSessionAudio,
  startPlacementSession,
  endPlacementSession,
  pauseSpeechSession,
  resumeSpeechSession,
  resetPlacementTranscript,
  endLocalAsrIntervention,
  initSocketCounters,
  getDisconnects,
  placementProxy,
  initUniversalSocket,
  startRecordingSession,
  buildAdhocAudio,
  saveComprehensionTextInput,
  saveTestletItemResponse,
  sendRelevelStoryCompleteToAos,
  startNetworkMonitoringCooldown,
  initLocalPlacementAsr,
  startLocalAsrPhrase,
  disposeLocalPlacementAsr,
  isLocalAsrRuntimeReady,
} from '../../../services/SpeechStream';
import { getUserData, getUtterance, shouldUseNewSpanishPlacementManagerForCurrentSession } from "../../../services/API";
import { useGenerativeVoiceGlobal } from '../../../services/generativeVoiceService';
import { userTypes } from "../../../constants/constants";
import { dispatchTtsRequestEnded } from '../../../services/ttsEvents';
import {isiPad, supportsSpeechRec} from "../../../services/environment";
import {logSessionInfo} from '../../../services/logger';
import { getUserLocale } from '../../../services/environment';
import { isMockAOSNow } from '../../../services/AssessmentOrchestrationService';
import * as Sentry from '@sentry/browser';
import device from '../../../services/device/device';
import {COPY_STRINGS} from '../../../constants/translatedCopy';

// redux dependencies
import { connect } from 'react-redux';
import speechCreators from './state/creators';
import { logSentryException, shouldForceHTML5Audio, createSilentWavBlob } from '../../../services/util';
import { FEATURE_FLAGS } from '../../../constants/featureFlags';
import {
  CLIPPED_PHRASE_P90_PCT,
  PERVASIVE_PHRASE_P90_PCT,
  PERVASIVE_PHRASE_SHARE,
  PERVASIVE_MIN_PHRASES,
  FLATLINE_MIN_RUN_SEC,
} from '../../../constants/audioDetectorThresholds';
import {
  shouldUseLocalAsr,
  shouldUseLocalAsrForNextPhrase,
  getLocalAsrWarmupRouteDecision,
  isLocalAsrProvisionallyServerRouted,
  LOCAL_ASR_ROUTE,
} from '../../../services/CursorOrchestrator';
import { getLocalAsrBackendId } from '../../../services/asr/asrBackend';
import { resolveLocalAsrPlacementLocale } from '../../../services/asr/localAsrEnablement';
import { flushLocalAsrLifecycle } from '../../../services/asr/localAsrLifecycle';

var stringSimilarity = require('string-similarity');

const myRecorder = new LexAudioRecorder({preset: 'speech_recognition'});

let recordingSession = {};
let previousRecordingSession = {};
let buildAudio = false;
let endMetadata = null;
let onSpeechStart = null;
let silentSegmentRun = 0;

const CONSECUTIVE_SILENT_THRESHOLD = 20;

let overallSilentSegments = 0;
let nullSegments = 0;
let totalSegments = 0;
let mutedRetries = 0;
let disableBackend = false;
// Distinguishes the two producers of disableBackend: a one-word intervention
// transcript request (true) versus a phrase re-read that is withheld from the
// backend, e.g. ReaderManager.tryAgain (false). Local ASR routes on this rather
// than on disableBackend, because a re-read is a phrase and must decode against
// the ordered phrase graph instead of intervention mode's single-word contract.
let interventionAudio = false;
// The single word the active intervention listen is judging (Listen.listener.targetWord).
// Threaded onto the recording props so local ASR can prefer it when choosing which
// token of a phrase-constrained decode to emit. Null outside an intervention.
let interventionTargetWord = null;
let usePhonASR = false;
let phonemePlacementEnabled = false;

let speechQueue = [];
let speechPending = false;
let abortSpeechPending = null;
let abortSpeechGeneration = 0;
let nextSpeechRequestId = 0;
let currentPendingSpeechRequestId = null;
let dancePending = false;
let struggleMonitorEnabled = false;
let abortDancePending = false;
let isMounted = false;
let loggedOut = false;

// this is used for phoneme placement when we have phrases which are a mix of phonemes and blended words.
// we combine the two transcripts here: standard placement for the blended word and phoneme asr for the phonemes
let pastCombinedPhonemeTranscript = "";

let sound = null;
// Bumped per playAppSounds call, so its watchdog can tell "still the current unit" from
// "superseded" without relying on `sound` identity (not yet assigned during construction).
let appSoundGeneration = 0;
// All armed watchdogs, not just the newest: an interrupting call orphans the previous timer, and
// only componentWillUnmount clears them.
const armedAppSoundTimeouts = new Set();
let audioDestination = null;
let audioInputMonitoringInitialized = false;
let currentAudioInputDevice = null;

// low volume recording constants
let lowRmsSegmentCount = 0;
let totalSegmentCount = 0;

// static recording constants
let staticSegmentCountByType = {
  lowBand: 0,
  broadband: 0,
};

// clipped audio constants
let badClippedSegments = 0; // count of flagged bad clipped segments
const INTELLIGIBILITY_SEVERITY_THRESHOLD = 60; // Severity score indicating bad clipping
const RMS_GATE_THRESHOLD_DB = -20; // Minimum RMS to consider clipping significant
const CLIPPING_WINDOW_SIZE = 25; // Rolling window size (segments) ~5s
const BAD_SEGMENT_THRESHOLD = 15; // Flag if 15/25 segments are bad (60%) ~3s

// Bump when the shape of the phrase/activity audio-metrics payloads changes, so a consumer
// reading a mix of old and new records can tell them apart. 1 = TT-1110's nested
// rawFeaturePercentiles shape; 2 = TT-1230's flattened, AppSync-shaped subset.
const AUDIO_METRICS_SCHEMA_VERSION = 2;

// TT-1230's candidate heuristics, logged as decisions beside the shipped isClipped/isLowVolume
// flags. Dark launch: computed and logged, read by nothing.
//
// isPervasivelyClipped and isFlatlined are complete decisions -- against the 79 by-ear labels
// they cost 1/45 and 0/45 false positives respectively, so they stand alone. hasClippedPhrase is
// NOT a decision and is deliberately named as an observation: it is only the clipping half of a
// rule whose other half, per-phrase story-word overlap, isn't computed client-side yet. Alone it
// catches 28/34 labelled-bad but also fires on 25/45 labelled-good.
const AUDIO_RULE_VERSION = 1;
// The four clipping boundaries come from constants/audioDetectorThresholds, which
// services/cursor/signalThresholds imports too: TT-991 replays these same cuts against TT-1256's
// stored payload, and the live and stored readings of one activity must not differ.

// TT-1110: activity-wide buffer of per-segment audio-quality values (severity/clipping/static
// inputs + db), superset of the population _checkClippedAudio uses (also keeps lowVolumeOrMuted
// segments, so percentiles reflect the true distribution rather than only the "loud enough" one).
// Never truncated mid-activity. Each record carries its own phraseIndex (stamped from
// evt.detail.props.phraseIndex) rather than being sliced by a timing-based cursor -- the encoder
// worker flushes a phrase's trailing segment asynchronously (recorder.js's stop() only posts to
// the worker; _exportWav reads whatever recorder.js's _recordingSession holds when the worker's
// reply arrives, not at buffer-capture time), often after onPhraseEnd's synchronous body has
// already moved on to the next phrase. This is a large improvement over a position-based cursor,
// but it is NOT an absolute per-buffer guarantee: what actually protects a phrase boundary is
// that disableRecording(false)'s recordingSession={} (empty) doesn't overwrite _recordingSession
// (recorder.js only replaces it for a non-empty props object), so the trailing reply keeps
// phrase N's props until the next phrase's start() supplies real ones. A worker reply still
// pending when that next start() fires would get stamped N+1 instead -- very unlikely given the
// EDM/scoring gap between phrases, but a residual, not a guarantee.
// Unbounded for the life of the activity: ~5.4 records/s at the 8192-sample default (~186ms at
// 44.1kHz), so a long continuous activity holds on the order of 10k objects, and every call to
// getActivityAudioMetrics() (not just at story end -- also per-testlet, via computeAudioMetrics)
// does several map+filter+sort passes over the whole thing. Not observed to be a problem, but
// nothing currently bounds or logs if it became one.
let segmentMetricsBuffer = [];
// Population that actually reached the flatline pipeline (passed the NaN/no-data guards in
// _collectFlatlineWindow) -- distinct from totalSegments, which also counts !data/done/
// buildAudio segments never analyzed. Exposed alongside elapsedActivitySec so offline analysis
// isn't stuck reconciling two silently-different populations in one payload.
let analyzedSegmentCount = 0;

// TT-988 flatline/dead-run candidate check -- dark-launch only (computed + logged, no action
// wired). This started as a sweep of a (IQR, level) grid so the threshold could be picked from
// logs without re-running audio analysis. TT-1230 picked it: across 166 sampled activities with
// a nonzero flat run, the loosest combo was the argmax of longestRunSeconds every single time,
// so the other 11 rows only ever restated a shorter prefix of the same run. The arrays stay
// arrays purely so a future sweep is a one-line change.
const FLATLINE_WINDOW_SIZE = 25; // trailing segments, same cadence as CLIPPING_WINDOW_SIZE (~5s)
const FLATLINE_IQR_THRESHOLDS_DB = [3];
const FLATLINE_LEVEL_THRESHOLDS_DB = [-50];
// There was a second, fractional branch here: fire when the longest flat run covered at least
// half a session of 60s or more. At 30s it is unreachable -- 60s elapsed puts the half-session
// bar at 30s, which the absolute branch has already cleared -- so it was removed rather than
// left in the payload advertising a rule parameter that cannot change any outcome. It would
// become meaningful again only if FLATLINE_MIN_RUN_SEC rose above half the elapsed floor.
// db is 20*log10(rms), unclamped -- digital silence (a hard-muted/disconnected mic, exactly
// the case this check exists for) yields -Infinity, not a very-negative-but-finite number.
// Segments are floored to this value rather than dropped; see _collectFlatlineWindow.
const FLATLINE_SILENCE_FLOOR_DB = -120;
let flatlineDbWindow = []; // trailing FLATLINE_WINDOW_SIZE db values, incl. quiet/allZeros segments
let flatlineGrid = buildFlatlineGrid(); // one row of running state per (iqr, level) combo
// Cumulative count of allZeros segments seen by _collectFlatlineWindow specifically (requires
// evt.detail.data to be present, unlike overallSilentSegments elsewhere in this file which also
// counts !data cases) -- see the phrase/activity aggregate's allZerosCount.
let allZerosSegmentCount = 0;
// Which DSP path produced this activity's spectralFlatness, which refreshes unconditionally on
// the AudioWorklet path but only if(this._analyser) on the ScriptProcessor path. Still worth
// keeping now that the spectral features themselves are no longer logged: severeSegFrac and
// badClippedSegments both read intelligibilitySeverity, and that is computed from
// spectralFlatness (recorder.js _calculateIntelligibilitySeverity), so the two paths still
// produce subtly different definitions of the same field. null until the first segment reports.
let capturePathUsingAudioWorklet = null;

// Snapshot of the last phrase boundary for the two counters that CAN'T be attributed to a
// phraseIndex the way segmentMetricsBuffer records can: badClippedSegments is the output of a
// rolling window that can legitimately span a phrase boundary (by design -- a dead-mic run or
// a clipping streak isn't phrase-scoped), and allZeros segments are never pushed to
// segmentMetricsBuffer at all. getPhraseAudioMetrics() reports these two as a snapshot delta,
// same residual timing sensitivity as before this fix for everything else. Everything else
// (percentiles, severeSegFrac, lowRmsSegmentCount, staticSegmentCountByType) is now derived by
// filtering segmentMetricsBuffer on its own stamped phraseIndex instead.
// Invariant: phrase deltas can never go negative only because this is re-zeroed in the same
// fullReset branch as the counters it snapshots (_clearClippedCounts) -- if either resets
// without the other, deltas will be silently wrong for the rest of the activity.
let phraseStartCounts = {
  badClippedSegments: 0,
  allZerosSegmentCount: 0,
};

// Retry/readback paths (ReaderManager.js) decrement lastEndedPhrase without advancing
// overallPhraseIndex, so onPhraseEnd -- and this file's phraseIndex stamping -- can run twice
// for the same phraseIndex. The two emissions are NOT equivalent: segmentMetricsBuffer-derived
// fields (filtered by phraseIndex) cover both attempts on the second call, while
// badClippedSegments/allZerosCount (snapshot deltas) cover only the retry, since the first
// call's clearPhraseAudioMetrics() already advanced that snapshot. Since logSessionInfo keys
// each call by inType + Date.now(), both emissions persist in S3 under the same
// audio_quality_phrase_<n> prefix. emissionIndex (1 = first attempt, 2 = retry, ...) is
// included in the payload so offline analysis can tell these apart instead of silently
// double-counting or mixing populations on exactly the phrases retries are triggered by.
let phraseEmissionCounts = {};

// Same sorted-array quantile approach as the offline analyzer
// (scripts/audio-quality-analysis/lib/activity-analyzer.js), for consistency.
function quantile(sortedValues, q) {
  if (!sortedValues.length) return null;
  const idx = Math.min(sortedValues.length - 1, Math.floor(q * sortedValues.length));
  return sortedValues[idx];
}

function sortedFinite(values) {
  return values.filter(Number.isFinite).sort((a, b) => a - b);
}

// Nearest-rank p75-p25, not the textbook (interpolated) IQR -- on a 25-sample window this picks
// indices 18 and 6, not fractional ranks. Deliberate: matches the offline analyzer's own
// quantile() (scripts/audio-quality-analysis/lib/activity-analyzer.js) so the two stay
// comparable. Not a bug to "fix" later.
function iqr(sortedValues) {
  if (!sortedValues.length) return null;
  return quantile(sortedValues, 0.75) - quantile(sortedValues, 0.25);
}

// Percentiles of the three features worth keeping, over whatever slice of
// segmentMetricsBuffer is passed in (a phrase's slice, or the whole activity).
//
// TT-1110 logged 11 feature families so the choice could be made from data. TT-1230 made it:
// swept against 79 by-ear labels with thresholds fitted out of sample, nothing OR-ed onto the
// rule reaches an activity it hasn't already reached, and substituting any family for clipping
// moves it by less than a coin flip. Clipping is what branches 1 and 2 read; db backs the
// low-volume and flatline branches.
//
// intelligibilitySeverity is kept for a different reason than the other two: it is not a rule
// input, it is the input to severeSegFrac, which bakes in INTELLIGIBILITY_SEVERITY_THRESHOLD
// and RMS_GATE_THRESHOLD_DB. A fraction can't be re-cut at a different severity or gate, so
// logging only severeSegFrac would mean any retune of those two constants needs a client build
// and a fresh collection window -- which is exactly what activeThresholds exists to avoid. It
// was also the closest of the 11 to beating clipping in the sweep, so it is the family most
// likely to be asked about again.
//
// Flat scalar names rather than nested {feature: {stat}} objects: these are destined for
// AppSync columns, and the nesting only ever existed to hold a sweep that has now concluded.
function audioPercentiles(records) {
  const clipping = sortedFinite(records.map((r) => r.clippingPercentage));
  const db = sortedFinite(records.map((r) => r.db));
  const severity = sortedFinite(records.map((r) => r.intelligibilitySeverity));
  return {
    clippingP50: quantile(clipping, 0.5),
    clippingP90: quantile(clipping, 0.9),
    dbP10: quantile(db, 0.1),
    dbP50: quantile(db, 0.5),
    dbP90: quantile(db, 0.9),
    // The only count worth carrying. db is the one feature that goes non-finite in normal
    // operation -- an empty buffer during a giveWord intervention yields NaN rms -- and
    // sortedFinite drops those while segmentCount still counts them, so without this a phrase
    // whose level was largely unmeasurable is indistinguishable from a clean one. Measured at
    // 0.1% of phrase records, worst case 1 segment in 15, but it costs one integer.
    dbCount: db.length,
    severityP50: quantile(severity, 0.5),
    severityP90: quantile(severity, 0.9),
  };
}

/**
 * Every phrase's own clippingP90, grouped out of the activity buffer by each record's stamped
 * phraseIndex. The TT-1230 clipping heuristics are phrase-level tests ("any phrase over X",
 * "half the phrases over Y"), and those can't be recovered from the activity-wide percentile --
 * one badly clipped phrase inside a long clean activity barely moves the pooled p90.
 *
 * Records with no phraseIndex are skipped (see unattributedSegmentCount). A phrase emitted twice
 * (retry/readback) pools both attempts into one percentile rather than being scored per attempt;
 * the offline analysis took the worst attempt, so a phrase clipped on only one of two attempts
 * reads slightly lower here.
 */
function phraseClippingP90Rows() {
  const byPhrase = new Map();
  for (const record of segmentMetricsBuffer) {
    if (record.phraseIndex == null) continue;
    const values = byPhrase.get(record.phraseIndex);
    if (values) {
      values.push(record.clippingPercentage);
    } else {
      byPhrase.set(record.phraseIndex, [record.clippingPercentage]);
    }
  }
  const rows = [];
  for (const [phraseIndex, values] of byPhrase) {
    const p90 = quantile(sortedFinite(values), 0.9);
    if (Number.isFinite(p90)) rows.push({ phraseIndex, clippingP90: p90 });
  }
  return rows.sort((a, b) => a.phraseIndex - b.phraseIndex);
}

function phraseClippingP90s() {
  return phraseClippingP90Rows().map((row) => row.clippingP90);
}

// Matches _checkClippedAudio's population exactly (excludes lowVolumeOrMuted, in addition to
// the allZeros/done/buildAudio exclusion already applied before a record is ever pushed) --
// a lowVolumeOrMuted segment can never satisfy the clip condition below, so including it would
// only dilute the denominator relative to the population the real windowed detector observes.
function isClipEligibleRecord(record) {
  return !record.lowVolumeOrMuted;
}

// Fraction of segments meeting the raw per-segment clipping condition *before* the
// CLIPPING_WINDOW_SIZE/BAD_SEGMENT_THRESHOLD rolling window is applied -- the window is known
// (PR #5813's offline analysis) to smooth away burst clipping, so this un-windowed rate lets the
// window parameters themselves be retuned offline later.
function computeSevereSegFrac(records) {
  const eligible = records.filter(isClipEligibleRecord);
  if (!eligible.length) return null;
  const severeCount = eligible.filter(
    (r) => r.intelligibilitySeverity >= INTELLIGIBILITY_SEVERITY_THRESHOLD && r.db >= RMS_GATE_THRESHOLD_DB
  ).length;
  return severeCount / eligible.length;
}

function buildFlatlineGrid() {
  const rows = [];
  for (const iqrThresholdDb of FLATLINE_IQR_THRESHOLDS_DB) {
    for (const levelThresholdDb of FLATLINE_LEVEL_THRESHOLDS_DB) {
      rows.push({
        iqrThresholdDb,
        levelThresholdDb,
        currentRunWindows: 0,
        longestRunWindows: 0,
        flatWindowCount: 0,
        totalWindowCount: 0,
      });
    }
  }
  return rows;
}

class Speech extends React.Component {
  constructor (props){
    super(props);

    this.locale = getUserLocale();

    this.state = {
      isListening: false,
      isSpeaking: false,
      isQuiet: true,
      abortSpeech: [],
      abortDance: () => {},
      matchRequest: null,
      transcript: {
        interim: null,
        final: null,
      },
      lexaSpeechProps: {}, //TODO: this is a temporary solution, eventually lexa speech will be a function we can invoke without updating props
      avatarSpeechProps: {
        textToSpeak: null,
      },
      pendingStartListening: false,
      pendingStartListeningSkipCallback: false,
      functions: {
        setAbortSpeech: this.setAbortSpeech.bind(this),
        setAbortDance: this.setAbortDance.bind(this),
      },
      buildAudio: false,
      blankRecording: false,
      altPlacement: false,
      forceKaldi: false,
      placementConnected: false,
      speechStreamFailure: false,
      onSpeechStreamFailure: null,
      inBackground: false,
      waitingForAsrConnection: false,
    };
    
    // Track if we need to wait for ASR connection (set when stopListening is called)
    this.needsAsrConnectionMonitor = false;
    
    // Track pending listening setup (when waiting for ASR connection)
    this.pendingListeningStateSetup = null;
    
    // Timeout for ASR connection wait
    this.asrConnectionTimeout = null;
    
    // Interval for kickstart polling
    this.kickstartPollInterval = null;
    
    // Counter for kickstart attempts
    this.kickstartAttempts = 0;

    this.setAvatarSpeech = this.setAvatarSpeech.bind(this);
    this.speakWithPromise = this.speakWithPromise.bind(this);
    this.playAppSounds = this.playAppSounds.bind(this);
    this.playAppSoundsWithPromise = this.playAppSoundsWithPromise.bind(this);
    this.onFinishAppSounds = this.onFinishAppSounds.bind(this);
    this.initializeAudioInputMonitoring = this.initializeAudioInputMonitoring.bind(this);
    this.handleAudioInputChange = this.handleAudioInputChange.bind(this);
    this.onStreamReady = this.onStreamReady.bind(this);
    this.updateCurrentAudioDevice = this.updateCurrentAudioDevice.bind(this);
    this.getAudioInputDeviceFingerprint = this.getAudioInputDeviceFingerprint.bind(this);
    this.createDeviceFingerprint = this.createDeviceFingerprint.bind(this);
    this.isSameDevice = this.isSameDevice.bind(this);
    this.handleMicIconStateChange = this.handleMicIconStateChange.bind(this);
    this.resetRecorderErrorLogs = this.resetRecorderErrorLogs.bind(this);
    this.handleAsrConnected = this.handleAsrConnected.bind(this);
    this.getActivityAudioMetrics = this.getActivityAudioMetrics.bind(this);
    this._checkClippedAudio = this._checkClippedAudio.bind(this);
    this.warmLocalPlacementAsr = this.warmLocalPlacementAsr.bind(this);
  }

  componentDidMount(){
    isMounted = true;
    console.log("speech mounted");
    // export some functions and info via the provided function
    //TODO: consider using redux here
    this.initPlacement();

    myRecorder.ondataavailable = {handler: this.onDataReady.bind(this)};
    myRecorder.onstop = {handler: this.onRecorderStop.bind(this)};
    myRecorder.onerror = {handler: this.onRecorderError.bind(this)};
    myRecorder.onquiet = {handler: this.onQuiet.bind(this)};
    myRecorder.onunquiet = {handler: this.onUnquiet.bind(this)};
    myRecorder.onmute = {handler: this.onMute.bind(this)};
    myRecorder.onunmute = {handler: this.onUnmute.bind(this)};
    myRecorder.onstreamready = {handler: this.onStreamReady.bind(this)};
    myRecorder.ontrackmuted = {handler: this.onTrackMuted.bind(this)};
    myRecorder.onaudiofileended = {handler: this.onAudioFileEnded.bind(this)}; // this method is used to detect when the mock audio file ends

    window.addEventListener('micIconStateChange', this.handleMicIconStateChange);
    window.addEventListener('asrConnected', this.handleAsrConnected);

    buildAudio = false;
    silentSegmentRun = 0; // also reset per activity in resetMuteDetection; silence detection
                          // spans phrases within an activity, not across activities.
    this.setState({altPlacement: this.useAltPlacement()});
    this.warmLocalPlacementAsr();
  }

  componentWillUnmount(){
    console.log("unmounting speech");
    isMounted = false;
    //Get rid of all the handlers we constructed
    myRecorder.ondataavailable = {remove: true};
    myRecorder.onstop = {remove: true};
    myRecorder.onerror = {remove: true};
    myRecorder.onquiet = {remove: true};
    myRecorder.onunquiet = {remove: true};
    myRecorder.onmute = {remove: true};
    myRecorder.onunmute = {remove: true};
    myRecorder.onstreamready = {remove: true};
    myRecorder.ontrackmuted = {remove: true};
    myRecorder.onaudiofileended = {remove: true};

    window.removeEventListener('micIconStateChange', this.handleMicIconStateChange);
    window.removeEventListener('asrConnected', this.handleAsrConnected);
    
    // Clean up audio input device change listener
    if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
      navigator.mediaDevices.removeEventListener('devicechange', this.handleAudioInputChange);
    }
    
    // Reset audio monitoring variables for potential re-mounting
    audioInputMonitoringInitialized = false;
    currentAudioInputDevice = null;
    
    // Reset ASR connection tracking
    this.needsAsrConnectionMonitor = false;
    this.pendingListeningStateSetup = null;
    
    // Clear ASR connection timeout if it exists
    if (this.asrConnectionTimeout) {
      clearTimeout(this.asrConnectionTimeout);
      this.asrConnectionTimeout = null;
    }
    
    // Clear kickstart polling interval if it exists
    if (this.kickstartPollInterval) {
      clearInterval(this.kickstartPollInterval);
      this.kickstartPollInterval = null;
    }
    
    this.kickstartAttempts = 0;
    
    disposeLocalPlacementAsr();

    myRecorder.stop();
    myRecorder.closeAudioContext();

    loggedOut = true;

    // Settles an in-flight playMarkupSequence unit that is on the AUDIO path: its poll compares
    // against this generation, and Reader's repeat path passes no isStale, so this is that path's
    // only unblock on teardown. A sequence parked on a TEXT unit is NOT covered — speakViaTts has
    // no poller and relies on speech.speak calling back — so that case still waits on the TTS
    // layer. Narrow by design: adding a second poller here is the thing the follow-up should
    // replace with a registered abort listener, not duplicate.
    abortSpeechGeneration++;

    // The only place the watchdog should not survive: firing after this would touch the recorder
    // stopped above. A pending playAppSoundsWithPromise never settles as a result. That is
    // deliberate rather than an oversight: settling it would mean invoking the caller's onfinish,
    // and chain-advancing callers (Phrase.speakHumanPhonemes) treat onfinish as "play the next
    // one" — which during teardown is exactly the resurrection this guard exists to prevent.
    // <Speech> is rendered unconditionally in App.js, so unmount is app teardown in practice.
    armedAppSoundTimeouts.forEach((handle) => clearTimeout(handle));
    armedAppSoundTimeouts.clear();

    if(sound){
      this.destroySound();
    }
  }

  componentDidUpdate(prevProps,prevState){
    // if we have new transcription info, pass it along
    if((this.props.transcript && this.props.transcript !== prevProps.transcript)
        || (!this.state.altPlacement && (this.props.finalTranscript && this.props.finalTranscript !== prevProps.finalTranscript))
        || (this.state.altPlacement && (this.state.finalTranscript && this.state.finalTranscript !== prevState.finalTranscript))
        || (usePhonASR && this.state.phonASRPhonemes && prevState.phonASRPhonemes != this.state.phonASRPhonemes)){ // if we're using the phonASR, then this logic should trigger when that updates rather than when the transcript updates
      if(this.state.transcriptRequest){
        let transcriptToSend = this.state.altPlacement ? this.state.finalTranscript : this.props.interimTranscript;
        if(usePhonASR) transcriptToSend = this.state.phonASRPhonemes;
        this.state.transcriptRequest.callback(transcriptToSend);
      }
      //If the speech component has an active match request, don't propagate props to
      // anyone else listening. This was put in to fix a problem in the reader
      // during the speech selection menu.
      // TODO: Really think about this. It would be nice to listen for phrases like "Lexa, help" on top of the student's reading.
      if(this.state.matchRequest){
        if((this.props.finalTranscript)
            || ((this.state.matchRequest.checkForMatch === false) && this.props.transcript)){
          this.checkMatch();
        }
      }else{
        let finalState = this.state.altPlacement ? this.state.finalTranscript : this.props.finalTranscript;
        let interimState = this.state.altPlacement ? this.state.interimTranscript : this.props.transcript;

        // if phoneme placement is enabled, blend the phoneme asr and placement transcripts together
        if(this.getPhonemePlacementEnabled()){
          const blendedTranscripts = this.blendTranscriptWithPhonemes(finalState, interimState);
          finalState = blendedTranscripts.final;
          interimState = blendedTranscripts.interim;
        }

        this.setState({
          transcript: {
            final: finalState,
            interim: interimState,
          },
        });
      }
    }
    if(this.props.forceKaldi !== prevProps.forceKaldi){
      this.setState({forceKaldi: this.props.forceKaldi},this.initPlacement);
    }

    if (this.props.localAsrReady && this.props.localAsrReady !== prevProps.localAsrReady) {
      this.warmLocalPlacementAsr();
    }
  }

  /**
   * Attaches to the local ASR engine once App reports it ready. Since TT-1313 / CR-10679
   * the engine is BOOTED by App behind the loading gate (SpeechStream's
   * ensureLocalPlacementAsrBooted), before "Let's do it" enables, so by the time
   * localAsrReady flips true the shared engine already exists and initLocalPlacementAsr
   * returns immediately — this is a reuse, not a boot. It stays as the belt-and-braces
   * path for a runtime that App marked ready but SpeechStream has not seen (dev remounts);
   * after an activity hard-failure it is a recorded no-op (the session is retired to
   * server ASR rather than re-booting the engine in the background).
   * @returns {void}
   */
  warmLocalPlacementAsr(){
    if (this.props.localAsrReady) {
      initLocalPlacementAsr();
    }
  }

  // PHONEME PLACEMENT LOGIC -- this will only be used if the story has a tag that has phoneme placement enabled
  //   if phoneme placement is enabled, blend the phoneme asr and placement transcripts together
  blendTranscriptWithPhonemes(finalTranscript, interimTranscript){
    let final = finalTranscript;
    let interim = interimTranscript;
    if(this.getPhonemePlacementEnabled()){ //belts and suspenders to make sure we don't do this unless we really want to

      // if we're currently in phoneme mode (because the current word is a phoneme),
      //  overwrite the transcript with the results from the phoneme ASR
      if(this.getPhonemeMode() && (this.state.phonASRPhonemes || this.state.phonASRPhonemes == '')) {
        final = this.state.phonASRPhonemes;
        interim = this.state.phonASRPhonemes;
      }

      // if we have accumulated a transcript from past phases of listening for phonemes or blended
      //  words in pastCombinedPhonemeTranscript, then prepend that value to our current transcript
      if(pastCombinedPhonemeTranscript && pastCombinedPhonemeTranscript != ""){
        final = pastCombinedPhonemeTranscript + " " + final;
        interim = pastCombinedPhonemeTranscript + " " + interim;
      }
    }
    return {final, interim};
  }

  initPlacement(){
    let useAltPlacement = this.useAltPlacement();

    //Inject alternate methods depending on the placement manager configuration
    const setGrammarFunction = useAltPlacement ? this.speechFunctionStub.bind(this) : this.props.setGrammar;
    const setOnSpeechStartFunction = useAltPlacement ? this.setOnSpeechStart.bind(this) : this.props.setOnSpeechStart;
    const setLanguageFunction = useAltPlacement ? this.speechFunctionStub.bind(this) : this.props.setLanguage;

    this.setState({
      altPlacement: useAltPlacement,
      numPhonemes: 0,
      abPhonemes: '',
      functions: {
        speak: this.queueSpeech.bind(this),
        speakWithPromise: this.speakWithPromise.bind(this),
        speakSequence: this.speakSequence.bind(this),
        prefetch: this.prefetch.bind(this),
        speakAudioFile: this.speakAudioFile.bind(this),
        startListening: this.startListening.bind(this),
        stopListening: this.stopListening.bind(this),
        setForceAOS: this.setForceAOS.bind(this),
        resetTranscript: this.resetTranscriptFunctionToExport.bind(this),
        setOnSpeechStart: setOnSpeechStartFunction,
        setGrammar: setGrammarFunction,
        setLanguage: setLanguageFunction,
        abortSpeech: this.abortSpeech.bind(this),
        // Read-only view of the abort-generation counter (bumped by abortSpeech) so
        // callers running their own audio loop can detect a mid-sequence abort.
        getAbortSpeechGeneration: getAbortSpeechGeneration,
        abortDance: this.abortDance.bind(this),
        setAbortDance: this.setAbortDance.bind(this),
        updateActivityId: this.updateActivityId.bind(this),
        initializeSpeechSession: this.initializeSpeechSession.bind(this),
        getPlacementConfig: this.getPlacementConfig.bind(this),
        requestTranscript: this.requestTranscript.bind(this),
        cancelTranscriptRequest: this.cancelTranscriptRequest.bind(this),
        requestMatch: this.requestMatch.bind(this),
        cancelMatchRequest: this.cancelMatchRequest.bind(this),
        enableRecording: this.enableRecording.bind(this),
        initializeRecorderIfNeeded: this.initializeRecorderIfNeeded.bind(this),
        saveCompTextResponse: this.saveCompTextResponse.bind(this),
        storeTestletItemResponse: this.storeTestletItemResponse.bind(this),
        onAosTriggerRelevelComplete: this.onAosTriggerRelevelComplete.bind(this),
        disableRecording: this.disableRecording.bind(this),
        buildAdhocRecording: this.buildAdhocRecording.bind(this),
        setAbortSpeech: this.setAbortSpeech.bind(this),
        isRecording: this.isRecording.bind(this),
        silentSegments: this.silentSegments.bind(this),
        volumeStatistics: this.volumeStatistics.bind(this),
        getSpeechState: this.getSpeechState.bind(this),
        playDance: this.playDance.bind(this),
        setOnScreenKeyboardOpen: this.setOnScreenKeyboardOpen.bind(this),
        onDanceStart: this.onDanceStart.bind(this),
        startVolumeProcessing: this.startVolumeProcessing.bind(this),
        stopVolumeProcessing: this.stopVolumeProcessing.bind(this),
        playAppSounds: this.playAppSounds.bind(this),
        playAppSoundsWithPromise: this.playAppSoundsWithPromise.bind(this),
        togglePhonemeListeningOn: this.togglePhonemeListeningOn.bind(this),
        togglePhonemeListeningOff: this.togglePhonemeListeningOff.bind(this),
        setPhonemePlacementEnabled: this.setPhonemePlacementEnabled.bind(this),
        getPhonemePlacementEnabled: this.getPhonemePlacementEnabled.bind(this),
        setAudioDestination: this.setAudioDestination.bind(this),
        clearAudioDestination: this.clearAudioDestination.bind(this),
        hasAudioDestination: this.hasAudioDestination.bind(this),
        setRecordingProps: this.setRecordingProps.bind(this),
        cancelRecorderStreamIfNeeded: this.cancelRecorderStreamIfNeeded.bind(this),
        restartRecorderStreamIfNeeded: this.restartRecorderStreamIfNeeded.bind(this),
        prepareMicForPlayback,
        prepareMicForRecord,
        getMicSourceType: this.getMicSourceType.bind(this),
        getRecorderErrorLogs: this.getRecorderErrorLogs.bind(this),
        setRecorderPreCheckStep: this.setRecorderPreCheckStep.bind(this),
        resetRecorderErrorLogs: this.resetRecorderErrorLogs.bind(this),
        getActivityAudioMetrics: this.getActivityAudioMetrics.bind(this),
        getAudioNetworkActivityFields: this.getAudioNetworkActivityFields.bind(this),
        getPhraseAudioMetrics: this.getPhraseAudioMetrics.bind(this),
        getPhraseClippingP90: this.getPhraseClippingP90.bind(this),
        getPhraseClippingP90s: this.getPhraseClippingP90s.bind(this),
        clearPhraseAudioMetrics: this.clearPhraseAudioMetrics.bind(this),
      },
    });
  }

  // Returns true if we should be using the Kaldi placement manager
  useAltPlacement(){
    // When running mock AOS, force Google PM so the backend can handle placement
    // without real AOS testlet context
    if (isMockAOSNow()) {
      return false;
    }

    //Assume that we're using Kaldi. This is the safest bet since if browser detect
    // fails the worst that will happen is that we'll fall back on chome, which is safe
    let useAltPlacement = true;
    let useStruggleMonitor = getUserData().useStruggleMonitor && getUserData().appSettings.useStruggleMonitor;

    //Switch between using Google's placement manager and Kaldi placement between the ORF
    // and the dyslexia test.
    if (device.browser) {
      if(supportsSpeechRec(device.browser) && ((!this.props.forceKaldi && !getUserData().tutorWhitelist && !useStruggleMonitor) || (this.props.forceGooglePM))){
        useAltPlacement = false;
      }
    }

    console.log("Kaldi:",useAltPlacement)
    return(useAltPlacement);
  }

  //This is used to stub out functions that aren't valid for the alt placement
  speechFunctionStub(){
  }

  setAvatarSpeech(avatarSpeechProps){
    this.setState({avatarSpeechProps});
  }

  onQuiet(evt){
    this.setState({
      isQuiet: true,
    });
  }

  onUnquiet(evt){
    this.setState({
      isQuiet: false,
    });
  }

  onMute(evt){
    console.log("MUTED",evt);
  }

  onUnmute(evt){
    console.log("UNMUTED",evt);
  }

  onTrackMuted(evt){
    console.log("TRACK_MUTED",evt);
    // Dispatch custom event that can be listened to by other components
    // Trigger the mic-muted error modal if the feature flag is enabled and the user is on the student pre-checks page
    if (FEATURE_FLAGS.ENABLE_MIC_MUTED_ERROR_MODAL && window.location.pathname.startsWith('/student/pre-checks')) {
      const trackMutedEvent = new CustomEvent('recorderTrackMuted', { detail: evt });
      window.dispatchEvent(trackMutedEvent);
    }
  }

  //************** Recording Methods */
  initializeRecorderIfNeeded(handleError, handleSuccess) {
    // For non-verbal users, override recorder methods to do nothing
    const userData = getUserData();
    if (userData && userData.nonverbal) {
      
      // Override loggingInfo getter to return safe default
      Object.defineProperty(myRecorder, 'loggingInfo', {
        get: function() {
          return {
            label: 'non-verbal-user-no-microphone',
            attributes: {}
          };
        },
        configurable: true
      });
      
      // Override other getters
      Object.defineProperty(myRecorder, 'sampleRate', {
        get: function() { return 0; },
        configurable: true
      });
      
      Object.defineProperty(myRecorder, 'bufferLength', {
        get: function() { return 0; },
        configurable: true
      });
      
      Object.defineProperty(myRecorder, 'volume', {
        get: function() {
          return {
            instant: 0.0,
            slow: 0.0,
            clip: 0.0,
            maxVolume: -Infinity,
            minVolume: Infinity,
            maxClip: 0
          };
        },
        configurable: true
      });
      
      // Override isRecording getter
      Object.defineProperty(myRecorder, 'isRecording', {
        get: function() { return false; },
        configurable: true
      });
      
      // Override isMicMuted getter
      Object.defineProperty(myRecorder, 'isMicMuted', {
        get: function() { return false; },
        configurable: true
      });
      
      // Set internal properties
      myRecorder._initialized = true;
      myRecorder._state = 'inactive';
      
      // Override methods to do nothing
      myRecorder.init = () => Promise.resolve();
      myRecorder.start = () => {};
      myRecorder.stop = () => {};
      myRecorder.closeAudioContext = () => {};
      myRecorder.cancelStream = () => {};
      myRecorder.restartStream = () => {};
      
      this.recorderInitialized = true;
      handleSuccess();
      return;
    }

    if (!loggedOut && !this.recorderInitialized) {
      myRecorder.init({preset: 'speech_recognition'})
        .then(()=>{
          this.recorderInitialized = true;
          handleSuccess()
        }).catch(error => {
        handleError(error)
      })
    }else{
      handleSuccess()
    }
  }

  // These two methods allow a component to control recording with an identifier that will be attached to any
  // and all audio fragments. Maybe startRecording
  enableRecording(recordingProps){
    recordingProps.clearHistory = true;
    recordingSession = recordingProps;
    disableBackend = recordingSession.disableBackend ? true : false;
    interventionAudio = false;
    interventionTargetWord = null;
    this.lastRecordingStoryLocale = recordingProps.storyLocale || null;
    // TT-1283: a phrase routed to server only because its biased graph is unavailable does
    // not wait for the Kaldi connection — the socket is effectively instant in prod, so
    // skip the wait/kickstart for it (see startListening's shouldWaitForAsrConnection).
    this._forceServerPlacementThisPhrase = Boolean(recordingProps.forceServerPlacement);

    startLocalAsrPhrase({
      ...recordingProps,
      locale: resolveLocalAsrPlacementLocale({ storyLocale: recordingProps.storyLocale }),
      enableAudioRecording: this.props.config.enableAudioRecording,
    });

    startRecordingSession();
    if((this.state.isListening) && (this.state.activityId)){
      myRecorder.start(recordingProps);
    }
  }

  saveCompTextResponse(payload, comprehensionCallback) {
    saveComprehensionTextInput(payload, comprehensionCallback);
  }

  storeTestletItemResponse(activityId, studentId, gradeLevel, testletId, itemId, itemType, expectedText, actualText, callback, locale = null) {
    saveTestletItemResponse(activityId, studentId, gradeLevel, testletId, itemId, itemType, expectedText, actualText, callback, locale);
  }

  onAosTriggerRelevelComplete(recordingMeta, activityId, studentId, gradeLevel, testletId, itemId, type, shouldUpLevel, shouldDownLevel, callback) {
    sendRelevelStoryCompleteToAos(recordingMeta, activityId, studentId, gradeLevel, testletId, itemId, type, shouldUpLevel, shouldDownLevel, callback);
  }

  disableRecording(inBuildAudio=false, inEndingMetadata=null){
    endMetadata = inEndingMetadata;
    buildAudio = inBuildAudio;
    //Handle the case where we're not recording but someone
    // wants to send the final packet to the backend
    if(!myRecorder.isRecording && inBuildAudio){
      this.onDataReady({
        detail: {
          props: recordingSession,
          data: new Blob([0],{type: 'audio/wav'}),
        },
      });
    }
    if(!inBuildAudio){
      recordingSession = {};
      disableBackend = false;
      interventionAudio = false;
      interventionTargetWord = null;
      // Comprehension listens complete through here, not through cancelTranscriptRequest, so
      // without this their intervention state stayed armed — and a wrap a stream still claims
      // is passed over by _evictLru, pinning a dead entry against a tight cache. Idempotent,
      // so "armed iff Speech thinks a listen is live" holds unconditionally.
      endLocalAsrIntervention();
      myRecorder.stop();
    }
  }

  buildAdhocRecording(activityId, metaObj) {
    buildAdhocAudio(activityId || this.state.activityId, metaObj || recordingSession);
  }

  isRecording(){
    return myRecorder.isRecording;
  }

  setOnSpeechStart(callback){
    onSpeechStart = callback;
  }

  // All audio quality flags for an activity are tracked here
  getActivityAudioMetrics(){
    // Records with no phraseIndex (e.g. startVolumeProcessing's volumeOnly mode replaces
    // _recordingSession with one carrying none) are invisible to every getPhraseAudioMetrics(n)
    // while still counted here -- so per-phrase segmentCounts can't be summed back to
    // totalSegmentCount without this to explain the gap.
    const unattributedSegmentCount = segmentMetricsBuffer.filter((r) => r.phraseIndex == null).length;
    const flatline = this._getFlatlineSummary();
    const phraseClippingP90 = phraseClippingP90s();
    const clippedPhraseCount = phraseClippingP90.filter((v) => v >= CLIPPED_PHRASE_P90_PCT).length;
    const pervasivelyClippedPhraseCount =
      phraseClippingP90.filter((v) => v >= PERVASIVE_PHRASE_P90_PCT).length;
    return {
      schemaVersion: AUDIO_METRICS_SCHEMA_VERSION,
      // These four names are a live contract with ReaderManager.computeAudioMetrics(), which
      // destructures them to derive isLowVolume/isStatic*/isClipped -- not just telemetry.
      lowRmsSegmentCount: lowRmsSegmentCount,
      totalSegmentCount: totalSegmentCount,
      staticSegmentCountByType: staticSegmentCountByType,
      badClippedSegments: badClippedSegments,
      ...audioPercentiles(segmentMetricsBuffer),
      severeSegFrac: computeSevereSegFrac(segmentMetricsBuffer),
      allZerosSegmentCount: allZerosSegmentCount,
      unattributedSegmentCount: unattributedSegmentCount,
      // Which DSP path produced the features this activity -- see the module-level comment on
      // capturePathUsingAudioWorklet.
      usingAudioWorklet: capturePathUsingAudioWorklet,
      // Logged once here, not per-phrase: activity-cumulative, so repeating it on every phrase
      // payload would just copy mostly-unchanged data onto every phrase's S3 PUT. Carries
      // isFlatlined, the third candidate heuristic below.
      ...flatline,

      // TT-1230 candidate heuristics -- decisions, sitting beside the shipped isClipped/
      // isLowVolume flags so the two generations can be compared on the same activity. Nothing
      // reads these. See the constants for which are verdicts and which is only a gate.
      audioRuleVersion: AUDIO_RULE_VERSION,
      hasClippedPhrase: clippedPhraseCount > 0,
      isPervasivelyClipped: phraseClippingP90.length >= PERVASIVE_MIN_PHRASES &&
        pervasivelyClippedPhraseCount / phraseClippingP90.length >= PERVASIVE_PHRASE_SHARE,
      // Counts, not just the booleans, so a flag can be audited without re-deriving it from the
      // phrase payloads -- and so "0 of 0 phrases" is distinguishable from "0 of 12".
      clippedPhraseCount,
      pervasivelyClippedPhraseCount,
      phrasesWithAudioCount: phraseClippingP90.length,

      activeThresholds: {
        intelligibilitySeverityThreshold: INTELLIGIBILITY_SEVERITY_THRESHOLD,
        rmsGateThresholdDb: RMS_GATE_THRESHOLD_DB,
        clippingWindowSize: CLIPPING_WINDOW_SIZE,
        badSegmentThreshold: BAD_SEGMENT_THRESHOLD,
        flatlineSilenceFloorDb: FLATLINE_SILENCE_FLOOR_DB,
        flatlineMinRunSec: FLATLINE_MIN_RUN_SEC,
        flatlineWindowSize: FLATLINE_WINDOW_SIZE,
        clippedPhraseP90Pct: CLIPPED_PHRASE_P90_PCT,
        pervasivePhraseP90Pct: PERVASIVE_PHRASE_P90_PCT,
        pervasivePhraseShare: PERVASIVE_PHRASE_SHARE,
        pervasiveMinPhrases: PERVASIVE_MIN_PHRASES,
      },
      // ReaderManager.js's computeAudioMetrics() merges its own thresholds into
      // activeThresholds above before this object reaches the audio_issue_detection_* log.
    };
  }

  // TT-1110: this phrase's slice of the activity-wide buffer, reduced to aggregates (percentiles
  // + counts) rather than raw per-segment values -- see plan doc for why. Filters
  // segmentMetricsBuffer by each record's own stamped phraseIndex rather than a position-based
  // slice: the encoder worker flush is always async and frequently arrives after onPhraseEnd's
  // synchronous body has moved on, so a segment's own phraseIndex -- not call ordering -- is
  // what attributes it correctly. This is a large improvement over a position cursor but not an
  // absolute per-buffer guarantee (see the module-level comment on segmentMetricsBuffer for the
  // actual, narrower mechanism). badClippedSegments/allZerosCount are the exception -- see the
  // comment on phraseStartCounts for why those two stay on the snapshot-delta approach.
  //
  // Retry/readback: ReaderManager.js can call this twice for the same phraseIndex (a retry
  // decrements lastEndedPhrase without advancing overallPhraseIndex). segmentMetricsBuffer is
  // never truncated, so the second call's phraseIndex-filtered fields cover BOTH attempts, while
  // badClippedSegments/allZerosCount (snapshot deltas) cover only the retry -- two different
  // populations in one payload, and both emissions persist in S3 under the same key prefix.
  // emissionIndex (1 = first attempt, 2 = retry, ...) makes this self-describing.
  // Story-end read for TT-1256. Does not bump emissionIndex — that counter is for the
  // per-phrase S3 log path in getPhraseAudioMetrics().
  getPhraseClippingP90s(){
    return phraseClippingP90Rows();
  }

  // Phrase-end persist: one phrase's p90, not a walk of the whole activity buffer.
  getPhraseClippingP90(phraseIndex) {
    const values = [];
    for (const record of segmentMetricsBuffer) {
      if (record.phraseIndex !== phraseIndex) continue;
      values.push(record.clippingPercentage);
    }
    const p90 = quantile(sortedFinite(values), 0.9);
    return Number.isFinite(p90) ? { phraseIndex, clippingP90: p90 } : null;
  }

  // O(1) counters + flatline summary. Phrase-end persist uses this instead of
  // getActivityAudioMetrics(), which also walks the full buffer for percentiles.
  getAudioNetworkActivityFields() {
    return {
      audioRuleVersion: AUDIO_RULE_VERSION,
      lowRmsSegmentCount,
      totalSegmentCount,
      flatlineMaxRunSec: this._getFlatlineSummary().flatlineMaxRunSec,
    };
  }

  getPhraseAudioMetrics(phraseIndex){
    phraseEmissionCounts[phraseIndex] = (phraseEmissionCounts[phraseIndex] || 0) + 1;
    const phraseRecords = segmentMetricsBuffer.filter((r) => r.phraseIndex === phraseIndex);
    const lowRmsInPhrase = phraseRecords.filter((r) => r.lowVolumeOrMuted).length;
    const staticInPhrase = { lowBand: 0, broadband: 0 };
    for (const r of phraseRecords) {
      if (!r.staticDetected || !r.staticType) continue;
      // Same 'low-band' -> 'lowBand' remap checkAndFlagNonVolume applies to the live event.
      const typeKey = r.staticType === 'low-band' ? 'lowBand' : r.staticType;
      if (typeKey in staticInPhrase) {
        staticInPhrase[typeKey]++;
      }
    }
    return {
      schemaVersion: AUDIO_METRICS_SCHEMA_VERSION,
      // Stamped into the payload, not just the S3 key, so a record stays self-describing once
      // it's a row rather than a file -- these become AppSync items keyed by activity.
      phraseIndex,
      emissionIndex: phraseEmissionCounts[phraseIndex],
      segmentCount: phraseRecords.length,
      allZerosCount: allZerosSegmentCount - phraseStartCounts.allZerosSegmentCount,
      ...audioPercentiles(phraseRecords),
      severeSegFrac: computeSevereSegFrac(phraseRecords),
      lowRmsSegmentCount: lowRmsInPhrase,
      staticLowBandCount: staticInPhrase.lowBand,
      staticBroadbandCount: staticInPhrase.broadband,
      badClippedSegments: badClippedSegments - phraseStartCounts.badClippedSegments,
      // Flatline and activeThresholds are activity-cumulative/static -- logged once via
      // getActivityAudioMetrics() instead of repeated on every phrase, see the comment there.
    };
  }

  // Advances the badClippedSegments/allZerosCount snapshot -- segmentMetricsBuffer itself is
  // never truncated (each record's own phraseIndex is what getPhraseAudioMetrics() filters on),
  // and this does NOT reset the activity-cumulative counters, which getActivityAudioMetrics()
  // still needs at story end.
  clearPhraseAudioMetrics(){
    phraseStartCounts = {
      badClippedSegments,
      allZerosSegmentCount,
    };
  }

  // Returns a flat, `flatline`-prefixed set of scalars that the caller spreads into the activity
  // payload -- no nested rows array. With the threshold grid collapsed to one combo (see
  // FLATLINE_IQR_THRESHOLDS_DB) there is only ever one row to report; taking the argmax of
  // longestRunSeconds keeps that correct rather than silently reporting an arbitrary row if
  // someone widens the arrays to re-run the sweep.
  _getFlatlineSummary(){
    // 0 (rather than null) when sampleRate isn't set yet (only assigned inside start()) --
    // segmentDurationSec/elapsedActivitySec are included in the payload specifically so a
    // 0-duration from this silently-missing case is distinguishable from "genuinely no flat
    // run found", rather than being indistinguishable as it would be from row data alone.
    const segmentDurationSec = this._getSegmentDurationSec();
    // elapsedActivitySec intentionally uses totalSegments (the whole session, matching TT-988's
    // "% of the session" framing), NOT analyzedSegmentCount (the narrower population that
    // actually fed the flatline windows -- excludes !data/done/buildAudio/NaN-db segments).
    // analyzedSegments is exposed alongside so offline analysis can reconcile the two rather
    // than silently assuming they're the same population flatWindowFraction is drawn from.
    const elapsedActivitySec = totalSegments * segmentDurationSec;
    const rows = flatlineGrid.map((row) => {
      // row.longestRunWindows counts window *evaluations*, not segments: the first window
      // only becomes full after FLATLINE_WINDOW_SIZE segments, so a run of N flat windows
      // spans N + FLATLINE_WINDOW_SIZE - 1 actual flat segments -- exact for a run starting at
      // segment 0 (the only case exercised by tests); for a run starting mid-activity, this can
      // overcount by up to FLATLINE_WINDOW_SIZE-1 segments (~5s) if the segments immediately
      // preceding the run weren't themselves flat. "Seconds" here means audio-analysis time
      // (segments actually fed to the flatline window), not wall-clock -- the window is never
      // reset across phrase boundaries or non-recording gaps (TTS, idle, page turns), so two
      // flat stretches separated by minutes of real time merge into one run.
      const longestRunSegments = row.longestRunWindows > 0
        ? row.longestRunWindows + FLATLINE_WINDOW_SIZE - 1
        : 0;
      const longestRunSeconds = longestRunSegments * segmentDurationSec;
      return {
        iqrThresholdDb: row.iqrThresholdDb,
        levelThresholdDb: row.levelThresholdDb,
        longestRunSegments,
        longestRunSeconds,
        flatWindowFraction: row.totalWindowCount ? row.flatWindowCount / row.totalWindowCount : null,
        // Note this is biased high by up to ~5s, per longestRunSegments above. That was ~5% slop
        // against the old 90s threshold; against 30s it is up to ~16%, so a run measured just
        // over the line may be just under it. The 0-of-45 false-positive figure is unaffected --
        // it was measured from this same statistic -- but a future fit reading
        // flatlineMaxRunSec as if it were unbiased seconds should know.
        wouldFireAtActivityLevel: longestRunSeconds >= FLATLINE_MIN_RUN_SEC,
      };
    });
    // Defensive `?? null` rather than an assumed rows[0]: this runs on the end-of-activity path,
    // where a throw would cost the whole audio_issue_detection_* record.
    const worst = rows.reduce((a, b) => (b.longestRunSeconds > a.longestRunSeconds ? b : a), rows[0]);
    return {
      flatlineElapsedActivitySec: elapsedActivitySec,
      flatlineAnalyzedSegments: analyzedSegmentCount,
      flatlineSegmentDurationSec: segmentDurationSec,
      flatlineIqrThresholdDb: worst?.iqrThresholdDb ?? null,
      flatlineLevelThresholdDb: worst?.levelThresholdDb ?? null,
      flatlineMaxRunSec: worst?.longestRunSeconds ?? null,
      flatlineFlatWindowFraction: worst?.flatWindowFraction ?? null,
      // Named with the is* family rather than wouldFire*: it is one of TT-1230's candidate
      // heuristic decisions, just one that happens to need no input beyond the flatline windows.
      // Falls back to false, not null, unlike the measurements above it: a decision that
      // serialises as null reads downstream as "unknown" on a column typed Boolean, and "we
      // evaluated no thresholds" is not evidence of dead air. Only reachable with empty
      // threshold arrays.
      isFlatlined: worst?.wouldFireAtActivityLevel ?? false,
    };
  }

  // Shared by _getFlatlineSummary and silentSegments -- 0 (not null) when sampleRate isn't set
  // yet (only assigned inside start()); callers that need to distinguish "unknown" from
  // "genuinely zero" should check myRecorder.sampleRate/bufferLength directly. A missing
  // bufferLength alone (sampleRate set but bufferLength not) yields NaN here, same as upstream.
  _getSegmentDurationSec(){
    return myRecorder.sampleRate ? myRecorder.bufferLength / myRecorder.sampleRate : 0;
  }

  // Activity-wide store of the per-segment values the aggregates are derived from, a superset of
  // the population _checkClippedAudio uses (also keeps lowVolumeOrMuted segments, so the
  // percentiles reflect the true distribution) -- see the module-level comment on
  // segmentMetricsBuffer for why. lowVolumeOrMuted is stored (not filtered out here) so
  // isClipEligibleRecord can filter severeSegFrac's population to match _checkClippedAudio's.
  // staticDetected/staticType are stored so getPhraseAudioMetrics() can derive a per-phrase
  // static count by filtering on phraseIndex, the same way it derives lowRmsSegmentCount.
  //
  // TT-1230 narrowed this from all 11 of TT-1110's features to the three that survive: clipping,
  // db and intelligibilitySeverity. All three feed audioPercentiles; the latter two also feed
  // computeSevereSegFrac. This buffer holds one object per ~186ms segment for the whole activity
  // (order 10k on a long one), so dropping the other eight -- nearClippingPercentage,
  // zcrDuringClipping, spectralFlatness, spectralRolloff, highLowFreqRatio, zcr, peakAmplitude
  // and crestFactorDB -- is a live-memory saving on low-end tablets, not just smaller logs.
  _collectSegmentMetrics(evt) {
    if (!evt?.detail?.data || evt.detail.allZeros || evt.detail.props?.done || buildAudio) {
      return;
    }
    const audioQuality = evt.detail.audioQuality;
    if (!audioQuality) {
      return;
    }
    const staticResult = evt.detail.staticResult;
    segmentMetricsBuffer.push({
      phraseIndex: evt.detail.props?.phraseIndex,
      db: audioQuality.rms,
      clippingPercentage: audioQuality.clippingPercentage,
      intelligibilitySeverity: audioQuality.intelligibilitySeverity,
      lowVolumeOrMuted: !!evt.detail.lowVolumeOrMuted,
      staticDetected: staticResult?.detected ?? false,
      staticType: staticResult?.type ?? null,
    });
  }

  // TT-988 flatline/dead-run candidate check (dark-launch, no action wired). Unlike
  // _collectSegmentMetrics, this intentionally does NOT exclude allZeros/lowVolume segments --
  // a dead/disconnected mic is exactly what shows up as those, and is the case this exists for.
  _collectFlatlineWindow(evt) {
    if (!evt?.detail?.data || evt.detail.props?.done || buildAudio) {
      return;
    }
    // Capture path is a session-level config, not audio content, so track it here (this
    // function's guard is the broadest of the collectors) regardless of allZeros/lowVolume.
    // Last-write-wins across the activity -- a mid-session fallback would show only its final
    // value, which is an accepted simplification, not something this check needs to resolve.
    if (evt.detail.usingAudioWorklet !== undefined) {
      capturePathUsingAudioWorklet = evt.detail.usingAudioWorklet;
    }
    if (evt.detail.allZeros) {
      allZerosSegmentCount++;
    }

    const rawDb = evt.detail.audioQuality?.rms;
    if (rawDb == null || Number.isNaN(rawDb)) {
      // Genuinely missing (e.g. an empty buffer during a giveWord intervention) -- not a
      // segment to floor, just nothing to measure.
      return;
    }
    analyzedSegmentCount++;
    // db is 20*log10(rms), unclamped: digital silence yields -Infinity, not a very-negative
    // finite number. A hard-muted/disconnected mic -- exactly the case this check exists for
    // -- produces all-zero buffers, so flooring rather than discarding is what makes those
    // segments register as maximally flat instead of silently vanishing from the window.
    const db = Number.isFinite(rawDb) ? rawDb : FLATLINE_SILENCE_FLOOR_DB;

    flatlineDbWindow.push(db);
    if (flatlineDbWindow.length > FLATLINE_WINDOW_SIZE) {
      flatlineDbWindow.shift();
    }
    if (flatlineDbWindow.length < FLATLINE_WINDOW_SIZE) {
      return;
    }

    const sortedWindow = [...flatlineDbWindow].sort((a, b) => a - b);
    const windowIqr = iqr(sortedWindow);
    const windowLevel = quantile(sortedWindow, 0.5);

    flatlineGrid.forEach((row) => {
      row.totalWindowCount++;
      if (windowIqr <= row.iqrThresholdDb && windowLevel <= row.levelThresholdDb) {
        row.flatWindowCount++;
        row.currentRunWindows++;
        if (row.currentRunWindows > row.longestRunWindows) {
          row.longestRunWindows = row.currentRunWindows;
        }
      } else {
        row.currentRunWindows = 0;
      }
    });
  }

  checkAndFlagNonVolume(evt) {
    // Don't track if this is the final segment or we're building audio OR if we've already flagged low volume for this activity
    // OR if the buffer is all zeros
    if (!evt?.detail?.data || evt.detail.allZeros || evt.detail.props?.done || buildAudio) {
      return;
    }

    // Track whether this segment is low volume in our sliding window
    const isLowVolume = evt.detail.lowVolumeOrMuted;
    totalSegmentCount++;
    if (isLowVolume) {
      lowRmsSegmentCount++;
    }

    const staticResult = evt.detail.staticResult;
    if (staticResult && staticResult.detected && staticResult.type) {
      // Map 'low-band' to 'lowBand' and 'broadband' to 'broadband'
      const typeKey = staticResult.type === 'low-band' ? 'lowBand' : staticResult.type;
      if (typeKey in staticSegmentCountByType) {
        staticSegmentCountByType[typeKey]++;
      }
    }
  }

  _checkClippedAudio(evt) {
    const audioQuality = evt.detail.audioQuality;
    if (!evt.detail.data || evt.detail.allZeros || evt.detail.lowVolumeOrMuted || evt.detail.props.done || buildAudio || !audioQuality) {
      return;
    }
    
    const { intelligibilitySeverity, rms } = audioQuality;
    
    // Initialize segment tracking array if not exists
    if (!this.clippedSegmentHistory) {
      this.clippedSegmentHistory = [];
    }
    
    // Track whether this segment has bad scores (INTELLIGIBILITY_SEVERITY_THRESHOLD or more for clipping AND RMS gate > RMS_GATE_THRESHOLD_DB)
    const hasClipping = intelligibilitySeverity >= INTELLIGIBILITY_SEVERITY_THRESHOLD && rms >= RMS_GATE_THRESHOLD_DB;
    
    // Add current segment status to history
    this.clippedSegmentHistory.push(hasClipping);
    
    // Keep only the last CLIPPING_WINDOW_SIZE segments in the rolling window
    if (this.clippedSegmentHistory.length > CLIPPING_WINDOW_SIZE) {
      this.clippedSegmentHistory.shift();
    }
    
    // Only check when we have exactly CLIPPING_WINDOW_SIZE segments
    if (this.clippedSegmentHistory.length === CLIPPING_WINDOW_SIZE) {
      // Count how many segments in the window have bad scores
      const badClippedCount = this.clippedSegmentHistory.filter(isBad => isBad).length;
      
      // If BAD_SEGMENT_THRESHOLD or more out of CLIPPING_WINDOW_SIZE segments are bad, flag this 5s segment
      if (badClippedCount >= BAD_SEGMENT_THRESHOLD) {
        badClippedSegments++;
        // Clear history after detecting a bad segment to avoid duplicate counting
        this.clippedSegmentHistory = [];
      }
      // Otherwise, keep the rolling window (oldest segment already shifted out above)
    }
  }

  _clearClippedCounts (fullReset = false) {
    // Clear the rolling window history
    this.clippedSegmentHistory = [];

    if (fullReset) {
      badClippedSegments = 0;
      staticSegmentCountByType = {
        lowBand: 0,
        broadband: 0,
      };
      // Moved from initializeSpeechSession so every counter this file resets for a new activity
      // resets in this one branch -- previously these two lines lived ~200 lines away from
      // this block despite phraseStartCounts' invariant comment claiming they were co-reset.
      // Except the mute counters -- resetMuteDetection also covers handoffs that skip this.
      lowRmsSegmentCount = 0;
      totalSegmentCount = 0;
      // TT-1110: reset the new activity-wide audio-quality tracking for the new activity
      segmentMetricsBuffer = [];
      analyzedSegmentCount = 0;
      flatlineDbWindow = [];
      flatlineGrid = buildFlatlineGrid();
      allZerosSegmentCount = 0;
      capturePathUsingAudioWorklet = null;
      phraseEmissionCounts = {};
      phraseStartCounts = {
        badClippedSegments: 0,
        allZerosSegmentCount: 0,
      };
    }
  }

  onDataReady(evt) {
    totalSegments++;
    this.checkAndFlagNonVolume(evt);
    this._checkClippedAudio(evt);
    this._collectSegmentMetrics(evt);
    this._collectFlatlineWindow(evt);
    //If the buffer contains all zeros and we're still actively sending, check for muted audio
    if((evt.detail.allZeros || !evt.detail.data) && !evt.detail.props.done && !buildAudio){
      overallSilentSegments++; //This gets logged at the end of the activity for analytics
      silentSegmentRun++;
      
      if((silentSegmentRun > CONSECUTIVE_SILENT_THRESHOLD) && !this.state.blankRecording){
        
        this.stopListening();
        if(mutedRetries == 0){
          this.stopListening();
          logSessionInfo("muted_retry",JSON.stringify({ ...myRecorder.loggingInfo }));
          
          
          myRecorder.init()
            .then(()=>{
              logSessionInfo("muted_retry_successful",JSON.stringify({ ...myRecorder.loggingInfo }));
              this.startListening();
              mutedRetries++;
              silentSegmentRun=0;
            })
            .catch((err)=>{
              mutedRetries++;
              logSentryException(new Error('Muted recording retry failure'), 'Muted retry failed', { originalError: err?.message, originalStack: err?.stack });
            });
        }else{
          this.setState({blankRecording: true});
          
          
          const loggingString = "MUTED RECORDING: " + this.state.activityId + ' ' + JSON.stringify(myRecorder.loggingInfo) + ' ' + overallSilentSegments;
          console.log(loggingString);
          Sentry.captureMessage("MUTED RECORDING");
          logSessionInfo("muted_recording", JSON.stringify({ loggingInfo: myRecorder.loggingInfo, silentSegments: overallSilentSegments }));
        }
      }
    } else if (!evt.detail.props.done && !buildAudio)  {
      silentSegmentRun = 0;
      mutedRetries=0;
    }

    //Only execute if there is a buffer to send
    if(evt.detail.data){
      //Check to see if we should send before we set the done flag below (to make sure the first "done" segment gets set)
      let shouldSend = !evt.detail.props.done || this.state.transcriptRequest;

      if((this.props.config.enableAudioRecording || buildAudio) && this.state.activityId){
        let recordingProps = evt.detail.props;
        recordingProps.activityId = this.state.activityId;
        recordingProps.disableBackend = disableBackend;
        recordingProps.interventionAudio = disableBackend && interventionAudio;
        // Deleted rather than nulled on non-intervention segments: recordingProps is
        // evt.detail.props, mutated in place and reused, so a stale target must not survive.
        // This keeps the field off every PHRASE segment; on intervention segments it does
        // still reach the socket payload (saveSessionAudio spreads metaObj), which is
        // accepted — disableBackend zeroes only SpeechStream/Nrt, so those segments still go
        // out whenever another service is on. It is an extra ignored field, not a contract
        // the backend reads.
        if (recordingProps.interventionAudio) {
          recordingProps.interventionTarget = interventionTargetWord;
        } else {
          delete recordingProps.interventionTarget;
        }

        // Allow done:true to be set even when allZeros is true - we need to send the done signal
        // to the backend regardless of whether the final audio segment is silent
        if(buildAudio && !this.state.transcriptRequest){
          recordingProps.done = true;
          logSessionInfo("recording_done", recordingProps);
          buildAudio=false;
          myRecorder.stop();
          recordingSession = {};
          if(endMetadata){
            recordingProps=Object.assign(recordingProps,endMetadata);
          }
          console.log("SENDING END OF PHRASE:",Date.now(),recordingProps); //TODO: Remove once confident in error detection
        }

        if(this.state.transcriptRequest){
          if(endMetadata){
            recordingProps=Object.assign(recordingProps,endMetadata);
          }
          recordingProps.done=false;
        }

        if(shouldSend){
          let onFailure = () => {
            this.stopListening();
            this.startListening();
            logSessionInfo("blob_error_recovered",{});
            this.setState({
              speechStreamFailure: true,
            });
          }
          saveSessionAudio(
            recordingProps.activityId,
            evt.detail.data,
            recordingProps,
            evt.detail.allZeros,
            onFailure,
            recordingProps.storyId,
            recordingProps.phraseIndex,
            evt.detail.samples16k,
          );
          if(recordingProps.comprehensionDone || recordingProps.comprehensionDiscard){
            recordingProps.comprehensionDone = false;
            recordingProps.comprehensionDiscard = false;
            recordingProps.discard = false;
            endMetadata = null;
          }

        }
      }
    }else{
      nullSegments++;
    }
  }

  getSpeechState(){
    return ({
      speechState: this.state,
      speechProps: this.props,
      recordingSession,
      buildAudio,
      endMetadata,
      silentSegmentRun,
      overallSilentSegments,
      nullSegments,
      totalSegments,
      mutedRetries,
      disableBackend,
    });
  }



  silentSegments(){
    let segmentDuration = this._getSegmentDurationSec();
    let timingOffset = nullSegments * segmentDuration * 1000;

    return {
      silentSegments: overallSilentSegments,
      nullSegments: nullSegments,
      totalSegments: totalSegments,
      sampleRate: myRecorder.sampleRate,
      bufferLength: myRecorder.bufferLength,
      segmentLength: segmentDuration,
      timingOffset: timingOffset,
      disconnects: getDisconnects()
    }
  }

  volumeStatistics(){
    return myRecorder.volume;
  }

  getRecorderErrorLogs(){
    return myRecorder.sentryMicErrorLogs || null;
  }

  resetRecorderErrorLogs() {
    myRecorder.sentryMicErrorLogs = {
      reinit_exceeded_msg: "",
      reinit_error_msg: "",
      context_resume_error_msg: "",
      listeningStarted: [],
      listeningStopped: [],
      trackErrorsOnStart: [],
      trackErrorsOnStop: [],
      micTurnedGreen: [],
      micTurnedGrey: [],
    };
  }

  setRecorderPreCheckStep(stepName){
    if (myRecorder && myRecorder.preCheckStep !== undefined) {
      myRecorder.preCheckStep = stepName;
      setPreCheckStep(stepName);
    }
  }

  onRecorderStop(evt){
    if(this.state.volumeOnly || (this.state.isListening || buildAudio) && ((Object.keys(recordingSession).length > 0) || this.state.transcriptRequest) && (this.state.activityId)){
      myRecorder.start(recordingSession);
    }
  }

  onRecorderError(evt){
    console.log("RECORDER ERROR: ",evt);
  }

  // A testlet handoff ends in trailing silence; carrying the run count into the next activity
  // trips the mute threshold on its first buffer, before the student can speak.
  resetMuteDetection(){
    silentSegmentRun = 0;
    mutedRetries = 0;
  }

  updateActivityId(activityId, callback){
    this.resetMuteDetection();
    this.setState({ activityId }, callback);
  }

  /**
   * @param {boolean} [spanishContent] Spanish content whatever `locale` says (the Spanish ASR
   *   check, GT-413): routes as an es-mx session would, off local ASR.
   */
  initializeSpeechSession(activityId,locale="en-us",useStruggleMonitor=false,useSpanishPlacement=false,useKaldiPhons=false,spanishContent=false){

    const doInitSpeechSession = () => {
      nullSegments = 0;
      totalSegments = 0;
      overallSilentSegments = 0;
      this.resetMuteDetection();
      struggleMonitorEnabled = useStruggleMonitor;

      const isSpanishSession = spanishContent || locale === "es-mx";
      let forceGoogleNRT = (this.state.altPlacement && isSpanishSession) && !useSpanishPlacement;
      let useSpanish = useSpanishPlacement && isSpanishSession;
      this.setState({activityId,forceGoogleNRT: forceGoogleNRT,useSpanish: useSpanish,useKaldiPhons,spanishContent});
      this._clearClippedCounts(true);
      initSocketCounters(activityId);
      logSessionInfo("media_info",myRecorder.loggingInfo);
      // Local ASR replaces the server placement manager outright for eligible phrases, so
      // it owns the label rather than appearing alongside "kaldi" — reading this log as
      // kaldi for an on-device session sent every ASR investigation to the wrong service.
      // It is checked first because it is the most specific case; the Spanish branches
      // below are unreachable for it in practice (local ASR is en-us only), but ordering
      // it first keeps that an explicit precedence rather than an accident of the locale
      // gates. `localAsrReady` separates "eligible and the runtime is up" from "eligible
      // but every phrase is still falling back to the server", which are indistinguishable
      // from the placement label alone.
      const localAsrEligible = !spanishContent && shouldUseLocalAsr({
        locale: resolveLocalAsrPlacementLocale(),
      });
      // The warmup routing decision (TT-1169), logged whichever way it went: a
      // session gated to the server reads localAsrEligible: false here exactly like
      // one that was never eligible at all, and this pair is what tells them apart —
      // "kaldi + verdict: slow" is the gate doing its job, not a licensing question.
      const warmupRouteDecision = getLocalAsrWarmupRouteDecision();
      // Every session local ASR APPLIED to, gated or not. A decision only exists
      // where the session was eligible enough to run a capability measurement, so
      // this recovers what localAsrEligible meant before the gate folded the verdict
      // into it, without changing what that field itself reports. The diagnostic
      // fields below hang off this rather than off localAsrEligible: a gated session
      // is precisely the one where "was the runtime even up?" is worth answering,
      // and gating them on the post-verdict answer blanked them exactly there.
      //
      // The provisional term is load-bearing for the same reason (TT-1218). An
      // unsettled overrun sets no decision, and the phrase gate that acts on it is
      // eligibility-blind — so on an ineligible phrase both other terms are false while
      // the gate still emits placement.phraseWarmupGated { provisional: true }. Without
      // this the disambiguating field below would be absent for exactly the session
      // shape whose gated rows are hardest to read.
      const localAsrConsidered = localAsrEligible
        || Boolean(warmupRouteDecision)
        || isLocalAsrProvisionallyServerRouted();
      let logString =  this.state.altPlacement ? "kaldi":"google";
      if(localAsrEligible){
        logString = "localAsr";
      }else if(forceGoogleNRT){
        logString = "googleNRT";
      }else if(useSpanish){
        logString = shouldUseNewSpanishPlacementManagerForCurrentSession(getUserData().assessmentMode, { spanishContent }) ? "amiraSpanish" : "googleSpanish";
      }

      logSessionInfo("placement_manager",{
        placement: logString,
        spanishNRT:forceGoogleNRT,
        useSpanish,
        useStruggleMonitor,
        localAsrEligible,
        localAsrBackend: localAsrConsidered ? getLocalAsrBackendId() : undefined,
        localAsrReady: localAsrConsidered ? isLocalAsrRuntimeReady() : undefined,
        // 'undecided' rather than absent while a measured session is still waiting
        // on its verdict. This checkpoint can run BEFORE the decision lands — the
        // deadline-overrun and props-driven paths both launch the activity with the
        // run still settling — and an absent field there is indistinguishable from a
        // session that measured FAST, which would leave a gated session reading as
        // placement: "localAsr" and undo the very labeling this pair exists for.
        // The four states are now distinct: undecided (routing provisional), local,
        // server (gated), and no field at all (local ASR never applied). All four
        // spellings come from LOCAL_ASR_ROUTE so a fleet query filtering this field
        // cannot disagree with the producer — UNDECIDED is report-only there and is
        // never a latched decision.
        localAsrWarmupRoute: localAsrConsidered
          ? (warmupRouteDecision?.route || LOCAL_ASR_ROUTE.UNDECIDED)
          : undefined,
        // Which KIND of undecided (TT-1218). 'undecided' above covers two states that
        // now route differently: a run still inside its deadline, which arms phrases
        // locally, and a run past it, which routes them to the server provisionally
        // until it settles. Without this the two are indistinguishable here, and a
        // provisionally-gated session reads as routing-neutral. Present only when it is
        // true, so it does not add a field to the sessions it says nothing about.
        localAsrWarmupDeadlineOverrun: localAsrConsidered
          ? (isLocalAsrProvisionallyServerRouted() || undefined)
          : undefined,
        localAsrWarmupVerdict: warmupRouteDecision?.verdict,
      });

      // Activity-start checkpoint for the local ASR lifecycle: re-emits the accumulated
      // pre-activity timeline (enablement, asset preload, runtime init) now that an
      // activityId exists, so it is keyed by the activity instead of by user only.
      //
      // Deliberately unconditional, including for sessions local ASR never applied to.
      // preloadLocalAsrAssets records the enablement decision and then returns before its
      // own flush when the session is blocked, so this is the ONLY checkpoint that
      // persists "why this activity ran on the server" — and an activityId is the entry
      // point an investigation actually starts from. The cost is one small object per
      // activity for those sessions, carrying just that decision.
      flushLocalAsrLifecycle('activityStarted');

      let loggingString = useStruggleMonitor && getUserData().useStruggleMonitor ? 'struggle_monitor_enabled' : 'struggle_monitor_disabled';

      logSessionInfo(loggingString, {
        customerSetting: getUserData().useStruggleMonitor,
        activityEnable: struggleMonitorEnabled
      });

      // Keep this as a fallback in case assets become ready after mount.
      this.warmLocalPlacementAsr();
    }

    // initialize recorder is normally called inside the ready module flow.
    // but in case of that goes away or skipped for whatever reason, recorder is initialized here.
    this.initializeRecorderIfNeeded((error)=>{
      console.error(error)
    }, doInitSpeechSession)
  }

  getPlacementConfig(){
    return({altPlacement: this.state.altPlacement, forceGoogleNRT: this.state.forceGoogleNRT, useSpanish: this.state.useSpanish});
  }

  // set the abortSpeech function passed up from LexaSpeech. This is a temporary solution while Lexa Speech is still a component
  setAbortSpeech(abortSpeech){
    let functionArray = this.state.abortSpeech;
    functionArray.push(abortSpeech);
    this.setState({abortSpeech: functionArray});
  }

  playDance(dance, onDanceEnd){
    dancePending = true;
    this.setState({
      avatarSpeechProps: {
        dance: dance,
        onDanceStart: this.onDanceStart,
        onDanceEnd: onDanceEnd
      }
    });
  }

  setOnScreenKeyboardOpen(isOpen) {
    this.setState({
      avatarSpeechProps: {
        ...this.state.avatarSpeechProps,
        oskOpen: isOpen
      }
    })
  }

  destroySound(){
    // Deliberately does NOT clear playAppSounds' watchdog: off() below removes the listeners that
    // would have, so afterwards the timer is the only thing that will settle callers who advance
    // purely from onfinish. It guards on appSoundGeneration instead of being disarmed here.
    if (sound) {
      sound.off();
      sound.stop();
      sound.unload();
      sound = null;
    }
  }

  onFinishAppSounds(props){
    // We must destroy the sound object before calling onfinish
    // because the onfinish callback could call playAppSounds again.
    this.destroySound();

    prepareMicForRecord();
    this.restartRecorderStreamIfNeeded();

    if (props.onfinish) {
      props.onfinish();
    }
  }

  playAppSounds(props, audioTimeoutDelay = 10000) {
    prepareMicForPlayback();
    this.cancelRecorderStreamIfNeeded();

    this.destroySound();

    // Separates "never became playable" (→ load error, caller falls back to TTS) from "was
    // audible but overran" (→ just finish) in the watchdog below.
    let startedPlaying = false;

    const myGeneration = ++appSoundGeneration;

    // Add timeout to prevent indefinite hanging
    const audioTimeout = setTimeout(() => {
      armedAppSoundTimeouts.delete(audioTimeout);

      // Superseded: a newer unit owns `sound` and the mic and does its own recorder restore, so
      // touch nothing — but still settle our caller, which has no other unblock.
      if (appSoundGeneration !== myGeneration) {
        if (props.onfinish) {
          props.onfinish();
        }
        return;
      }

      // Nothing left to tear down (aborted). Must still take the full finish path: that is what
      // restores the recorder stream this function stopped on the way in, on iPad.
      if (!sound) {
        this.onFinishAppSounds(props);
        return;
      }

      const reason = startedPlaying ? 'playback overrun' : 'load timeout';
      console.log(`playAppSounds ${reason} after ${audioTimeoutDelay}ms`);
      // Only the load-timeout kind is an error. An overrun is expected wherever a caller passes a
      // short deliberate cutoff (ReaderManager plays silence.mp3 with audioTimeoutDelay 2500).
      if (FEATURE_FLAGS.ENABLE_COMMON_SENTRY_ERROR_LOGGING && !startedPlaying) {
        // Message kept as-is so existing Sentry alerts keep matching; kind goes in extraData.
        logSentryException(new Error('App sounds playback timeout'), 'playAppSounds timeout', {
          timeoutKind: reason,
          url: props.url,
          externalURL: props.externalURL,
          audioTimeoutDelay,
        });
      }
      // Before the teardown: the caller must know this unit failed by the time its onfinish runs,
      // or replay takes the clean-finish path and plays silence. An overrun already played, so it
      // is not a load failure (and note the teardown truncates it mid-word — pre-existing).
      if (!startedPlaying && props.onLoadError) {
        props.onLoadError();
      }
      this.onFinishAppSounds(props);
    }, audioTimeoutDelay); // default to 10 seconds
    armedAppSoundTimeouts.add(audioTimeout);

    // Single disarm path, so a fifth exit cannot be added that clears the timer but forgets to
    // drop it from the registry (which would leave unmount clearing an already-fired handle).
    const disarm = () => {
      clearTimeout(audioTimeout);
      armedAppSoundTimeouts.delete(audioTimeout);
    };

    const hasMp3Support = Howler.codecs('mp3');

    const howl = new Howl({
      src: [props.externalURL || props.url],
      format: ['mp3'],
      volume: props.volume || (isiPad() ? 0.4 : 1),
      html5: shouldForceHTML5Audio(),
      pool: 1,
      onloaderror: (id, error) => {
        disarm();
        console.error('playAppSounds onload error:', error);
        console.error('Error details:', JSON.stringify({
          url: props.url,
          externalURL: props.externalURL,
          key: props.key,
          globalContextState: globalContext?.state,
          hasMp3Support: hasMp3Support,
        }));
        logSentryException(new Error('App sounds load failure'), 'playAppSounds onload error', { originalError: error?.message, originalStack: error?.stack });
        // Load failure = the asset is missing/corrupt/not-yet-deployed. Notify the optional
        // onLoadError hook so the caller can fall back (e.g. to TTS) instead of playing
        // silence; onfinish still fires afterward so callers without the hook don't hang.
        if (props.onLoadError) {
          props.onLoadError();
        }
        this.onFinishAppSounds(props);
      },
      onplayerror: (id, error) => {
        disarm();
        console.error('playAppSounds onplay error:', error);
        console.error('Error details:', JSON.stringify({
          url: props.url,
          externalURL: props.externalURL,
          key: props.key,
          globalContextState: globalContext?.state,
          hasMp3Support: hasMp3Support,
        }));
        logSentryException(new Error('App sounds play failure'), 'playAppSounds onplay error', { originalError: error?.message, originalStack: error?.stack });
        // The old sound.once('unlock', …) retry was removed: destroySound() below strips the
        // listener and nulls `sound`, so it could never fire. Report it as a load failure instead,
        // so replay falls back to TTS rather than silently advancing.
        if (props.onLoadError) {
          props.onLoadError();
        }
        this.onFinishAppSounds(props);
      },
      onload: () => {
        // Generation, not `sound`: Howler's _emit dispatches every handler through
        // setTimeout(fn, 0) with the fn bound at dispatch time, so off() empties the listener
        // arrays but CANNOT cancel a callback already queued. A superseded howl whose 'load'
        // was emitted before destroySound() therefore still runs this on the next macrotask —
        // and `sound` is by then the NEWER howl, so a bare `if (sound)` would call play() on
        // it and (pool: 1) restart the live unit mid-word.
        if(appSoundGeneration === myGeneration && sound) {
          sound.play();
        }
      },
      onplay: () => {
        // The watchdog stays armed (it also guards a playback that never ends), but from here
        // on a timeout is an overrun, not a load failure — see `startedPlaying` above.
        startedPlaying = true;
        if(props.onStart){
          props.onStart();
        }
      },
      onstop: () => {
        disarm();
        this.onFinishAppSounds(props)
      },
      onend: () => {
        disarm();
        this.onFinishAppSounds(props)
      },
    });

    // Defensive, and believed unreachable today: Howler's _emit defers every handler by a
    // setTimeout(fn, 0), so no constructor event can re-enter playAppSounds before this line and
    // bump the generation. Kept rather than deleted because it is the only thing standing between
    // a future synchronous emit (or a re-entrant caller reached some other way) and `sound` being
    // overwritten with a howl that has already been superseded.
    if (appSoundGeneration === myGeneration) {
      sound = howl;
    } else {
      // off() before unload(), same invariant as destroySound(): unload() leaves the listeners
      // attached and does not cancel an in-flight fetch. off() cannot retract an already-queued
      // callback either, which is why onload above checks the generation rather than trusting this.
      howl.off();
      howl.unload();
    }
  }

  playAppSoundsWithPromise(props, audioTimeoutDelay = 10000) { // Promise is is used instead of passing "onfinish" param in props
    const onfinishCallback = props.onfinish;
    return new Promise((resolve) => {
      this.playAppSounds({
        ...props,
        onfinish: () => {
          resolve();
          onfinishCallback && onfinishCallback();
        }
      }, audioTimeoutDelay);
    });
  }

  onDanceStart(){
    dancePending = false;
    if(abortDancePending){
      this.abortDance();
    }
  }

  abortDance(){
    if(dancePending){
      abortDancePending = true;
      return;
    }
    if(this.state.abortDance){
      this.state.abortDance();
    }
  }

  setAbortDance(abortDance){
    this.setState({abortDance: abortDance});
  }

  //either speaks the utterance (if the queue is empty), or enques it (if it is not)
  // to be the only exported speak() function
  // returnToListening is an optional param which defines whether or not we should start listening when the speech is finished
  queueSpeech(utterance, returnToListening = false, callback = null, forceQueue = false){
    if(this.state.isSpeaking || speechQueue.length > 0 || forceQueue){
      speechQueue.push({utterance: utterance, callback: callback});
    } else {
      this.setState({isSpeaking: true}, () => this.speak(utterance, returnToListening || this.state.isListening || this.state.waitingForAsrConnection, callback));
    }
  }

  speakWithPromise(utterance, returnToListening) { // Promise is used instead of passing onEnd in utterance object
    return new Promise((resolve) => {
      this.queueSpeech({
        ...utterance,
        onEnd: resolve,
        resolveAbort: resolve,
      }, returnToListening);
    });
  }

  /**
   * Prefetches TTS audio for the given text segments to warm the cache.
   * When speak() is later called with the same text, it will be an instant cache hit.
   * @param {string|string[]} textOrTexts - A single text string or array of text strings to prefetch
   * @param {Object} [options] - Optional settings
   * @param {boolean} [options.skipTranslation] - When true in bilingual mode, uses primary locale
   *   (matching speak() behavior for skipTranslation utterances). Default false uses bilingualLocale.
   * @param {string} [options.previousText] - Optional TTS context sent as previous_text.
   */
  prefetch(textOrTexts, options = {}) {
    const texts = Array.isArray(textOrTexts) ? textOrTexts : [textOrTexts];
    const { locale, bilingualLocale, bilingualMode } = getUserData();
    // Mirror speak() locale resolution: skipTranslation uses primary locale, otherwise bilingualLocale
    const inLocale = options.skipTranslation ? locale : (bilingualMode ? bilingualLocale : locale);
    const useGenerative = useGenerativeVoiceGlobal();
    
    texts.forEach(text => {
      if (text && text.trim()) {
        getUtterance(text.trim(), false, inLocale, 16000, useGenerative, options.previousText).catch((err) => {
          // Prefetch failures are non-blocking. If this request was in-flight when speak() called
          // getUtterance for the same key, both share the promise (utterance_inflight dedup) and
          // speak() receives the same error — its onError handler will fire. If prefetch completes
          // (success or failure) before speak(), speak() makes a fresh request on cache miss.
          logSessionInfo('tts_prefetch_error', { text: text.substring(0, 50), error: err?.message });
        });
      }
    });
  }

  speakSequence(speechProps){
    let speechArray = speechProps.textToSpeak;
    for(let i = 0; i < speechArray.length; i++){
      let speechSegment = speechArray[i];
      let segmentSpeechProps = {
        textToSpeak: speechSegment.textToSpeak,
        passThroughSpeechTags: speechProps.passThroughSpeechTags,
        skipTranslation: speechSegment.skipTranslation,
        onEnd: (i == speechArray.length - 1) && speechProps.onEnd,
        onError: speechProps.onError, // handle errors on any segment that failed
      }
      this.queueSpeech(segmentSpeechProps, undefined, undefined, i == 1); //force enqueue the second segment of speech
    }
  }

  /**
   * @deprecated should use the applyAppSounds method directly
   * Replaced the body with applyAppSounds, this is acting as an alias,
   * @param {*} props
   */
  speakAudioFile(props){
    this.playAppSounds({
      url: props.url,
      onStart: props.onStart,
      onfinish: props.onfinish,
      onLoadError: props.onLoadError
    });
  }

  //This is how we speak once we have chosen an utterance
  speak(utterance, returnToListening, callback){
    const speechRequestId = ++nextSpeechRequestId;
    speechPending = true;
    currentPendingSpeechRequestId = speechRequestId;
    
    // Tag each speech request so stale TTS cleanup cannot clear a newer in-flight request.
    utterance._speechRequestId = speechRequestId;
    utterance._abortGeneration = abortSpeechGeneration;
    
    const { locale, bilingualLocale, bilingualMode } = getUserData();
    if (!utterance.locale) utterance.locale = utterance.skipTranslation ? locale : (bilingualMode ? bilingualLocale : locale);
    utterance.skipAvatar = this.props.skipAvatar || utterance.skipAvatar; //This is necessary because we are accessing "speak" directly in so many places instead of going through "sendMessage"
    this.stopListening();
    function onStart(){
      if(currentPendingSpeechRequestId === speechRequestId){
        speechPending = false;
        currentPendingSpeechRequestId = null;
      }

      if(abortSpeechPending && abortSpeechPending.speechRequestId === speechRequestId){
        const abortProps = abortSpeechPending.props;
        abortSpeechPending = null;
        this.abortSpeech(() => {}, abortProps);
        return;
      }

      if(utterance.onStart){
        utterance.onStart();
      }
      this.stopListening();
    }
    function onEnd(){
      if(speechQueue.length > 0){
        let nextUtterance = speechQueue.shift();

        this.speak(nextUtterance.utterance, returnToListening, nextUtterance.callback);
        if(utterance.onEnd){
          utterance.onEnd();
        }
        if(callback){
          callback()
        };
      } else {
        let callbackWithListen = function (returnToListening,callback){
          // if we were listening when we started speaking, start listening again
          if(returnToListening || this.state.pendingStartListening){
            console.log("Resume listening because we were listening")
            const skipCallback = this.state.pendingStartListeningSkipCallback;
            this.setState({pendingStartListening: false, pendingStartListeningSkipCallback: false});
            this.startListening("", null, skipCallback);
          }else{
            console.log("Don't resume listening")
          }
          if(utterance.onEnd){
            utterance.onEnd();
          }
          if(callback){
            callback()
          };
        }
        this.setState({
          isSpeaking: false,
        }, callbackWithListen.bind(this,returnToListening,callback));
      }
    }
    let props = Object.assign({}, utterance);
    props.onStart = onStart.bind(this);
    props.onEnd = onEnd.bind(this, callback);
    
    this.setState({
      isSpeaking: true,
      lexaSpeechProps: props,
    });
  }

  // takes an optional callback. This is useful if you want to speak immediately after aborting
  // TODO this should also clear the queue
  abortSpeech(callback=null, props={}){
    abortSpeechGeneration++;
    
    if(speechPending){
      abortSpeechPending = {
        props: props,
        speechRequestId: currentPendingSpeechRequestId,
      };

      // TT-1166: barge-in while a TTS fetch is still pending (LexaSpeech hasn't resolved
      // yet, so no onUtteranceStart/End will fire for it) — dismiss the thinking bubble now.
      dispatchTtsRequestEnded(currentPendingSpeechRequestId);
    }

    this.destroySound();

    let itemsAborted = 0;
    let stateUpdated = false;
    let callbackActivated = false;
    let abortSpeechCallback = () => {
      itemsAborted++;
      if((this.state.abortSpeech && this.state.abortSpeech.length <= itemsAborted) || !this.state.abortSpeech){
        callbackActivated = true;
        if(stateUpdated){
          callback && callback();
        }
      }
    }
    speechQueue = [];
    
    if(this.state.abortSpeech && this.state.abortSpeech.length > 0){
      for(let i=0; i < this.state.abortSpeech.length; i++){
        this.state.abortSpeech[i](() => {
          abortSpeechCallback();
        });
      }
    } else {
      abortSpeechCallback();
    }

    //TODO: we MUST move this chunk of code. up into `abortSpeechCallback` above, and add `callback`
    //  to the setState callback. We can't do this until we have time for thorough regression testing though,
    //  and adding callbacks to aborts throughout the app. Then we can get rid of `stateUpdated` and `callbackActivated`
    this.setState({
      isSpeaking: false,
    }, () => {
      stateUpdated = true;
      if(callbackActivated){
        abortSpeechCallback();
      }
      if(props.returnToListening){
        this.startListening("abortSpeech returnToListening");
      }
    });
  }

  phonCallback(inPhonemes){
    this.setState({numPhonemes:inPhonemes});
  }

  amirabetCallback(inPhonemes){
    this.setState({abPhonemes: {phon: inPhonemes, time: Date.now()}});
  }

  phonASRcallback(inPhonemes){
    this.setState({phonASRPhonemes: inPhonemes});
  }

  transcriptCallback(transcript){
    this.setState({interimTranscript: transcript, finalTranscript: transcript});
  }

  setPhonemeMode(phonemeMode){
    usePhonASR = phonemeMode;
  }

  getPhonemeMode(phonemeMode){
    return usePhonASR;
  }

  resetPastCombinedPhonemeTranscript() {
    pastCombinedPhonemeTranscript = "";
  }

  togglePhonemeListeningOn(){
    //if we're already listening for phonemes, do nothing
    if(this.getPhonemeMode()){
      return;
    }

    // set the phoneme mode to true so that the next time we start listening we will use the phoneme asr
    this.setPhonemeMode(true);

    // stop and restart listening in order to make the change to phoneme mode take effect
    this.stopListening("togglePhonemeListeningOn", {
      onFinishCallback: () => {
        // if we have a transcript from listening with the standard placement asr, add it to
        //   our running combined transcript and then reset it
        if(this.props.transcript.final || this.props.transcript.interim){
          pastCombinedPhonemeTranscript += " " + (this.props.transcript.final || this.props.transcript.interim);
          //NOTE: this is deliberately not resetTranscriptFunctionToExport(), because that would
          //  also reset pastCombinedPhonemeTranscript, which we just appended to.
          // This fires on a word-index change mid-phrase, and phoneme placement (phrase type)
          // and local ASR (feature flag + en-US locale) are independent gates that can both be
          // on, so the wipe must not end the local ASR phrase — the student keeps reading it and
          // only enableRecording re-arms, which the startListening below is not.
          if(this.useAltPlacement()){
            this.resetTranscript({endLocalAsrPhrase: false});
          }else{
            this.props.resetTranscript && this.props.resetTranscript();
          }
        }
        this.startListening("togglePhonemeListeningOn");
      }
    });
  }

  togglePhonemeListeningOff(){
    //if we're already using standard placement, do nothing
    if(!this.getPhonemeMode()){
      return;
    }

    // set the phoneme mode to false so that the next time we start listening we will use the standard placement asr
    this.setPhonemeMode(false);

    // stop and restart listening in order to make the change to phoneme mode take effect
    this.stopListening("togglePhonemeListeningOff", {
      onFinishCallback: () => {
        // if we have a transcript from listening with the phoneme asr, add it to
        //   our running combined transcript
        if(this.state.phonASRPhonemes){
          pastCombinedPhonemeTranscript += " " + this.state.phonASRPhonemes;
        }
        this.resetPhonASRPhonemes({callback: this.startListening});
      },
    });
  }

  setPhonemePlacementEnabled(newPhonemePlacementEnabled){
    phonemePlacementEnabled = newPhonemePlacementEnabled;
    
    // Reset usePhonASR when disabling phoneme placement to prevent state leaking between stories.
    // This fixes the case where a phoneme story (UP_AND_DOWN/LETTER_FLIES) is followed by a
    // non-phoneme story (e.g., changeOnePart), ensuring usePhonASR doesn't incorrectly remain true.
    if(!newPhonemePlacementEnabled && usePhonASR){
      this.setPhonemeMode(false);
    }
  }

  getPhonemePlacementEnabled() {
    return phonemePlacementEnabled;
  }

  startListening(source = "", transcriptRequest=null, skipCallback=false, useComprehensionASR=false,comprehensionCallback=null, useAOS=false, enableAllServices=true, waitForAsr=false){
    let callback = null;
    if(!this.state.isSpeaking && speechQueue.length <= 0){ //enforce that we can only listen while we are not speaking
      if(this.state.altPlacement){
        if(onSpeechStart){
          onSpeechStart();
        }
        callback = ((transcript)=>{
          if (!skipCallback) {
            this.transcriptCallback(transcript);
          }
        }).bind(this);
      }else{
        this.props.startListening(); // start listening with the webspeech api
      }

      // Determine if we should wait for ASR connection before setting up listening state
      const isStandardTutorASRFlow = !useComprehensionASR && 
                                 !audioDestination && 
                                 !this.state.forceGoogleNRT && 
                                 !this.state.useSpanish && 
                                 !usePhonASR &&
                                 !useAOS && 
                                 !this.state.forceAOS &&
                                 !transcriptRequest;
      // Explicit locale: this.locale is session-specific (e.g. Spanish activities)
      // and may differ from the signed-in user's locale
      // The TRANSIENT question (TT-1218): the wait and kickstart poll below exist to get
      // Kaldi talking before the student does, so they must not read as local-owned while
      // an unsettled overrun has startLocalAsrPhrase routing the phrase to the server.
      // Named for the route rather than for eligibility because that is what it now holds
      // — conflating the two is the thing TT-1218 exists to stop doing.
      const nextPhraseUsesLocalAsr = shouldUseLocalAsrForNextPhrase({
        locale: resolveLocalAsrPlacementLocale({ storyLocale: this.lastRecordingStoryLocale }),
      });
      const shouldWaitForAsrConnection =  FEATURE_FLAGS.WAIT_FOR_ASR_CONNECTION &&
                                          this.needsAsrConnectionMonitor && 
                                          isStandardTutorASRFlow && 
                                          !nextPhraseUsesLocalAsr &&
                                          !this._forceServerPlacementThisPhrase &&
                                          waitForAsr;

      let useStruggleMonitor = getUserData().useStruggleMonitor && struggleMonitorEnabled;
      startPlacementSession(callback, this.phonCallback.bind(this), this.state.altPlacement && enableAllServices, this.state.forceGoogleNRT, this.amirabetCallback.bind(this),useStruggleMonitor,this.state.useSpanish,usePhonASR, useComprehensionASR,this.phonASRcallback.bind(this),audioDestination,comprehensionCallback,this.state.useKaldiPhons, useAOS || this.state.forceAOS, Boolean(this.state.spanishContent));

      // Create the listening setup function
      const setupListeningState = () => {
        this.setState((prevState) => ({
          isListening: true,
          waitingForAsrConnection: false,
          transcriptRequest: transcriptRequest || prevState.transcriptRequest,
        }),()=>{
          if(((Object.keys(recordingSession).length > 0) && (this.state.activityId)) ||
              (this.state.transcriptRequest))
          {
            myRecorder.start(recordingSession);
            resumeSpeechSession();
          }
          this.props.setIsListening(true);
        });
      };

      if (shouldWaitForAsrConnection) {
        this.setState({ 
          waitingForAsrConnection: true,
          isListening: false 
        });
        this.props.setIsListening(false);
        this.pendingListeningStateSetup = setupListeningState;

        // Set up timeout safeguard - if ASR doesn't respond within 15 seconds, proceed anyway
        if (this.asrConnectionTimeout) {
          clearTimeout(this.asrConnectionTimeout);
        }
        this.asrConnectionTimeout = setTimeout(() => {
          // Stop kickstart polling
          if (this.kickstartPollInterval) {
            clearInterval(this.kickstartPollInterval);
            this.kickstartPollInterval = null;
          }
          
          // Log to Sentry as this indicates a problem with ASR connection
          Sentry.captureMessage('ASR connection timeout - kaldiConnected event not received', {
            level: 'warning',
            tags: {
              activityId: this.state.activityId,
              storyId: recordingSession.storyId,
              phraseIndex: recordingSession.phraseIndex,
              kickstartAttempts: this.kickstartAttempts.toString(),
            },
          });
          if (this.pendingListeningStateSetup) {
            this.pendingListeningStateSetup();
            this.pendingListeningStateSetup = null;
            this.needsAsrConnectionMonitor = false;
            this.asrConnectionTimeout = null;
            this.kickstartAttempts = 0;
          }
        }, 15000);

        // Poll by sending kickstart segments every 200ms until ASR responds
        // This continuously "pokes" the backend until it's ready, rather than guessing a delay
        
        // Clear any existing kickstart poll interval
        if (this.kickstartPollInterval) {
          clearInterval(this.kickstartPollInterval);
        }
        
        // Reset attempt counter
        this.kickstartAttempts = 0;
        
        // Function to send a single kickstart segment
        const sendKickstartSegment = () => {
          // Make sure we're still in wait mode (could have been cancelled)
          if (!this.pendingListeningStateSetup) {
            if (this.kickstartPollInterval) {
              clearInterval(this.kickstartPollInterval);
              this.kickstartPollInterval = null;
            }
            return;
          }
          
          this.kickstartAttempts++;
          
          // Send initial audioSegment to kickstart ASR connection
          const kickstartBlob = createSilentWavBlob();
          const kickstartMetadata = {
            ...recordingSession,
            id: this.state.activityId, // Backend SpeechStream service requires id field
            activityId: this.state.activityId,
            isKickstartSegment: true, // Flag to identify kickstart segment for logging purposes
            kickstartAttempt: this.kickstartAttempts, // Track which attempt this is
          };
          
          saveSessionAudio(
            this.state.activityId,
            kickstartBlob,
            kickstartMetadata,
            false, // not all zeros
            null, // no failure callback needed
            recordingSession.storyId,
            recordingSession.phraseIndex
          );
        };
        
        // Send first kickstart immediately
        sendKickstartSegment();
        
        // Then poll every 200ms
        this.kickstartPollInterval = setInterval(sendKickstartSegment, 200);
      } else {
        // Set up listening state immediately
        setupListeningState();
      }
    }
    else{ //Someone tried to start listening while we were speaking. Update the state so we start listening when we're done
      this.setState({
        pendingStartListening: true,
        pendingStartListeningSkipCallback: skipCallback,
      });
    }
  }

  setForceAOS(forceAOS, callback=null){
    this.setState({
      forceAOS
    }, callback);
  }

  stopListening(source = "", additionalProps={}){
    this.needsAsrConnectionMonitor = true; // ASR connection will be reset
    this.pendingListeningStateSetup = null; // Clear any pending listening setup (this prevents stale kaldiConnected events from being processed)
    
    // Clear ASR connection timeout if it exists
    if (this.asrConnectionTimeout) {
      clearTimeout(this.asrConnectionTimeout);
      this.asrConnectionTimeout = null;
    }
    
    // Clear kickstart polling interval if it exists
    if (this.kickstartPollInterval) {
      clearInterval(this.kickstartPollInterval);
      this.kickstartPollInterval = null;
      this.kickstartAttempts = 0;
    }
    
    pauseSpeechSession();
    endPlacementSession();
    this.props.stopListening();
    this.props.setIsListening(false);
    this.setState({
      isListening: false,
      waitingForAsrConnection: false, // Reset waiting state when stopping
    },() => {
      myRecorder.stop();
      if(additionalProps.onFinishCallback){
        additionalProps.onFinishCallback();
      }
    });
  }

  handleAsrConnected(){
    // Only process this event if we have a pending callback waiting for it
    // This prevents us from processing stale kaldiConnected events that arrive after stopListening
    if (!this.pendingListeningStateSetup) {
      return;
    }
    
    // Clear the timeout since ASR connected successfully
    if (this.asrConnectionTimeout) {
      clearTimeout(this.asrConnectionTimeout);
      this.asrConnectionTimeout = null;
    }
    
    // Clear the kickstart polling interval since ASR connected
    if (this.kickstartPollInterval) {
      clearInterval(this.kickstartPollInterval);
      this.kickstartPollInterval = null;
    }
    
    this.setState({ waitingForAsrConnection: false });
    
    // ASR connection established, execute the pending listening setup
    this.pendingListeningStateSetup();
    this.pendingListeningStateSetup = null;
    this.needsAsrConnectionMonitor = false; // Reset the flag
    this.kickstartAttempts = 0; // Reset counter
  }

  requestTranscript(callback,phonemeMode=false,useComprehension=false,comprehensionCallback=null,targetWord=null){
    const startTranscriptRequest = () => {
      disableBackend = true;
      interventionAudio = true;
      // The word this listen is judging. Rides the recording props so local ASR can
      // pick the target out of a phrase-constrained decode; see Listen.listen.
      interventionTargetWord = targetWord || null;
      usePhonASR = phonemeMode;
      this.startListening("requestTranscript", {
        callback: callback,
      },false,useComprehension,comprehensionCallback);
      this.setState({
        previousTranscript: this.state.transcript.interim,
      });
    }

    // if we are already listening when the transcript request is made, stop listening before
    //   requesting the transcript to make sure that all the listen props get reset appropriately
    if(this.state.isListening){
      this.stopListening("requestTranscript", {onFinishCallback: startTranscriptRequest});
    } else {
      startTranscriptRequest();
    }
  }

  cancelTranscriptRequest(restoreTranscript){
    const {previousTranscript} = this.state;
    usePhonASR = false;
    this.stopListening("requestTranscript");
    this.setState({
      previousTranscript: null,
      transcriptRequest: null,
      phonASRPhonemes: null
    });
    if(restoreTranscript && previousTranscript){
      this.props.resetTranscript(previousTranscript);
    }
    disableBackend = false;
    interventionAudio = false;
    interventionTargetWord = null;
    // Tell local ASR the intervention is over. Every intervention end and abort routes
    // through here (Listen.handleMatches on resolve, Listen.abortListen on abort), so
    // this is the lifecycle signal the handler previously had to infer from audio
    // metadata — and could not, since intervention audio carries the phrase's props.
    endLocalAsrIntervention();
  }

  // starts listening for a match of the transcript to one of the strings in options[]
  // when a match is found with certainty at or above threshold, callback will be called
  // NOTE: there can only be one match request at a time
  requestMatch(options, threshold, callback,startListening=false,checkForMatch=true){
    if(startListening) this.startListening("requestMatch");

    this.setState({
      matchRequest: {
        options: options,
        threshold: threshold,
        callback: callback,
        checkForMatch: checkForMatch,
      }
    });
  }

  cancelMatchRequest(){
    this.setState({
      matchRequest: null,
    });
  }

  // returns the index of the option closest to the current final transcript,
  //   with a certaintanty of cutoff. If no match is found, returns -1
  checkMatch(){
    let transcript = this.state.matchRequest.checkForMatch == true ? this.props.finalTranscript : this.props.transcript;

    if(transcript.length === 0){
      return;
    }

    let matches = stringSimilarity.findBestMatch(transcript, this.state.matchRequest.options);

    if(!this.state.matchRequest.checkForMatch){
      this.props.resetTranscript();
    }

    if(matches.bestMatch.rating >= this.state.matchRequest.threshold){
      this.state.matchRequest.callback(
          this.state.matchRequest.options.indexOf(matches.bestMatch.target)
      );
      this.cancelMatchRequest();
    } else{
      this.state.matchRequest.callback(-1);
    }
  }

  resetTranscript({ endLocalAsrPhrase = true } = {}){
    resetPlacementTranscript({ endLocalAsrPhrase });
    this.setState({
      numPhonemes: 0,
      abPhonemes: '',
      transcript: {
        final: null,
        interim: null,
      },
    });
  }

  // options are alt-placement only: props.resetTranscript takes a transcript string,
  // so the local ASR flag must never reach it.
  resetTranscriptFunctionToExport(options){
    this.resetPastCombinedPhonemeTranscript();
    if(this.useAltPlacement()){
      this.resetTranscript(options);
    }else{
      this.props.resetTranscript && this.props.resetTranscript();
    }
  }

  resetPhonASRPhonemes({callback=null}){
    this.setState({
      phonASRPhonemes: null,
    }, callback);
  }

  // Set of methods to allow the application to run the recorder solely for audio measurements
  startVolumeProcessing(){
    this.setState({volumeOnly: true},()=>{myRecorder.start({voumeOnly: true, clearHistory: true})});
  }

  stopVolumeProcessing(){
    this.setState({volumeOnly: false},()=>{myRecorder.stop()});
  }

  setAudioDestination(destination) {
    audioDestination = destination;
  }

  clearAudioDestination() {
    audioDestination = null;
  }

  hasAudioDestination() {
    return audioDestination !== null;
  }

  setRecordingProps(props) {
    recordingSession = {...recordingSession, ...props};
  }

  cancelRecorderStreamIfNeeded() {
    if (isiPad()) {
      myRecorder.cancelStream();
    }
  }

  restartRecorderStreamIfNeeded() {
    if (isiPad()) {
      myRecorder.restartStream();
    }
  }

  /**
   * Get the current microphone source type for logging purposes
   * Returns the device label from the recorder (e.g., 'Built-in Microphone', 'USB Audio Device', 'iPad Microphone')
   * or 'unknown' if no device information is available
   */
  getMicSourceType() {
    if (myRecorder && myRecorder.loggingInfo && myRecorder.loggingInfo.label) {
      return myRecorder.loggingInfo.label;
    }
    return 'unknown';
  }

  /**
   * Called when the audio stream is ready - this is the perfect time to set up device monitoring
   * since we know getUserMedia has been called and devices are enumerated
   * Note: This can be called multiple times due to stream restarts, so we use a flag to ensure
   * the event listener is only set up once, but we update the current device reference each time
   */
  onStreamReady() {
    if (!audioInputMonitoringInitialized) {
      this.initializeAudioInputMonitoring();
    } else {
      // Stream was restarted - update our current device reference
      this.updateCurrentAudioDevice();
    }
  }

  /**
   * Called when audio file playback ends (only in audio file mode)
   * This automatically stops the current activity/story when the audio file finishes
   */
  onAudioFileEnded(evt) {
    console.info('Audio file ended - auto-completing activity', evt.detail);
    
    // Stop listening immediately
    if (this.state.isListening) {
      console.info('Stopping listening due to audio file end');
      this.stopListening('audio_file_ended');
    }
    
    const metrics = this.getActivityAudioMetrics();
    console.info('Activity audio metrics:', metrics);
  }

  /**
   * Initialize audio input device monitoring for detecting device changes
   * This monitors when users unplug headphones or switch audio devices
   * This method is called after the audio stream is ready to avoid early getUserMedia calls
   */
  async initializeAudioInputMonitoring() {
    // Check if browser supports the required APIs
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      console.warn('Audio input monitoring not supported in this browser');
      return;
    }

    try {
      // Set up the initial device reference
      await this.updateCurrentAudioDevice();

      // Set up device change listener
      navigator.mediaDevices.addEventListener('devicechange', this.handleAudioInputChange);
      
      // Mark as initialized to prevent duplicate setup
      audioInputMonitoringInitialized = true;
    } catch (error) {
      console.warn('Failed to initialize audio input monitoring:', error);
    }
  }

  /**
   * Handle audio input device changes
   * This is called when the browser detects media device changes (e.g., unplugging headphones)
   * 
   * Logs every device change for debugging and monitoring purposes.
   * The log includes device fingerprints (label, device count, position) to track what changed.
   */
  async handleAudioInputChange() {
    // If we don't have a current device fingerprint yet, nothing to compare
    if (!currentAudioInputDevice) {
      return;
    }

    // Get fingerprint for the device that should be at the same position
    const currentFingerprint = await this.getAudioInputDeviceFingerprint({
      deviceIndex: currentAudioInputDevice.deviceIndex,
    });
    
    // Check if the current device is still the same
    const deviceChanged = !this.isSameDevice(currentAudioInputDevice, currentFingerprint);
    
    if (deviceChanged) {
      // Capture previous device before updating to avoid stale closure in logs
      const previousDevice = currentAudioInputDevice;
      
      // Log the audio input change detection
      logSessionInfo('audio_input_change_detected', {
        previousDevice: previousDevice,
        currentDevice: currentFingerprint,
        sampleRate: globalContext?.sampleRate,
        message: 'Audio input device changed',
      });
      
      // Update current device to track future changes
      currentAudioInputDevice = currentFingerprint;
      
      // Immediately restart listening if currently listening to recover the audio stream
      // This follows the same pattern as the muted retry logic
      if (this.state.isListening) {
        console.log('Restarting listening due to audio input change');
        logSessionInfo('audio_input_change_recovery_started', {
          previousDevice: previousDevice,
          currentDevice: currentFingerprint,
        });
        
        // Stop listening to prepare for reinitialization
        this.stopListening('audio_input_change');

        // Suppress bad-network analysis while recorder reinitializes to avoid false positives
        // from stale audio queue data during the device switch (TT-179)
        startNetworkMonitoringCooldown(5000);
        
        // Re-initialize the stream with the new audio device
        myRecorder.init()
          .then(() => {
            logSessionInfo('audio_input_change_recovery_success', {
              device: currentFingerprint,
            });
            this.startListening('audio_input_change');
          })
          .catch((err) => {
            logSentryException(new Error('Audio input change recovery failure'), 'Audio input change recovery failed', { originalError: err?.message, originalStack: err?.stack });
            logSessionInfo('audio_input_change_recovery_failed', { 
              error: err.message,
              device: currentFingerprint,
            });
          });
      }
    }
  }

  /**
   * Helper function to enumerate audio input devices and create a fingerprint
   * @param {Object} options - Configuration options
   * @param {number} options.deviceIndex - If provided, gets device at this index
   * @param {boolean} options.getPrimary - If true, gets the primary device (first with label)
   * @returns {Promise<Object|null>} Device fingerprint or null if not found
   */
  async getAudioInputDeviceFingerprint({ deviceIndex = null, getPrimary = false } = {}) {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputDevices = devices.filter(device => device.kind === 'audioinput');
      
      let targetDevice = null;
      
      if (getPrimary) {
        // Get the primary device (first with a label, or first device)
        if (audioInputDevices.length > 0) {
          targetDevice = audioInputDevices.find(device => device.label) || audioInputDevices[0];
        }
      } else if (deviceIndex !== null) {
        // Get device at specific index
        targetDevice = audioInputDevices[deviceIndex];
      }
      
      if (!targetDevice) {
        return null;
      }
      
      return this.createDeviceFingerprint(devices, targetDevice);
    } catch (error) {
      console.warn('Error getting audio input device fingerprint:', error);
      return null;
    }
  }

  /**
   * Create a device fingerprint that's more stable than deviceId alone
   * Uses label + device count + position to identify the same physical device
   */
  createDeviceFingerprint(devices, targetDevice) {
    const audioInputDevices = devices.filter(device => device.kind === 'audioinput');
    const deviceIndex = audioInputDevices.findIndex(device => device.deviceId === targetDevice.deviceId);
    
    return {
      label: targetDevice.label || 'unknown',
      deviceCount: audioInputDevices.length,
      deviceIndex: deviceIndex,
    };
  }

  /**
   * Check if two device fingerprints likely represent the same physical device
   */
  isSameDevice(fingerprint1, fingerprint2) {
    if (!fingerprint1 || !fingerprint2) return false;
    
    // If labels are available and match, very likely the same device
    if (fingerprint1.label && fingerprint2.label && fingerprint1.label !== 'unknown') {
      return fingerprint1.label === fingerprint2.label;
    }
    
    // If device count changed, likely a device was added/removed
    if (fingerprint1.deviceCount !== fingerprint2.deviceCount) {
      return false;
    }
    
    // If same position and same device count, likely the same device
    return fingerprint1.deviceIndex === fingerprint2.deviceIndex;
  }

  /**
   * Update the current audio input device reference
   * This is called when the audio stream is restarted to ensure we're tracking the correct device
   */
  async updateCurrentAudioDevice() {
    const fingerprint = await this.getAudioInputDeviceFingerprint({ getPrimary: true });
    if (fingerprint) {
      currentAudioInputDevice = fingerprint;
    }
  }

  handleMicIconStateChange(event) {
    const isListening = event.detail.isListening;
    if (isListening) {
      myRecorder.sentryMicErrorLogs.micTurnedGreen.push(Date.now()); // log the time when the mic is turned green
    } else {
      myRecorder.sentryMicErrorLogs.micTurnedGrey.push(Date.now()); // log the time when the mic is turned grey
    }
  }

  render() {
    const { isListening, isSpeaking, isQuiet, transcript, avatarSpeechProps, blankRecording, altPlacement, numPhonemes, abPhonemes, speechStreamFailure, inBackground, waitingForAsrConnection } = this.state;

    let isMicMuted = myRecorder.isMicMuted;

    const propsToPassDown = {
      ...this.state.functions,
      isListening,
      isSpeaking,
      isQuiet,
      avatarSpeechProps,
      isMicMuted,
      blankRecording,
      altPlacement,
      numPhonemes,
      abPhonemes,
      speechStreamFailure,
      inBackground,
      waitingForAsrConnection,
    };

    return (
        <div className="speech">
          <LexaSpeech
              config={this.props.config}
              speechProps={this.state.lexaSpeechProps}
              setAbortSpeech={this.setAbortSpeech.bind(this)}
              sendToAvatar={this.setAvatarSpeech}
          />
          
          {this.props.children(
              propsToPassDown,
              !this.state.transcriptRequest ? transcript : {} // if there's an active transcript request, don't flow the transcript through to the children since it will flow through the callback function instead
          )}
        </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  setIsListening: isListening => dispatch(speechCreators.setIsListening(isListening)),
});

// this is necessary because we need to know whether phoneme placement mode is enabled in NLP.js
//   in order to correctly diff the expected text and the spoken text
export function getPhonemePlacementEnabled(){
  return phonemePlacementEnabled;
}

// Export function to check abort generation for in-flight TTS cancellation
export function getAbortSpeechGeneration(){
  return abortSpeechGeneration;
}

// Clear stale pending state only for the TTS request that was skipped.
export function clearPendingSpeechForSkippedRequest(speechRequestId){
  if(abortSpeechPending && abortSpeechPending.speechRequestId === speechRequestId){
    abortSpeechPending = null;
  }

  if(currentPendingSpeechRequestId === speechRequestId){
    speechPending = false;
    currentPendingSpeechRequestId = null;
  }
}

//Export the component here instead of at top so that we can wrap it in
// the speech recognition component
//
// this adds the following relevant properties to this component:
// transcript: final transcription of all speech that has been spoken into the microphone during the listening period.
// startListening(): Causes the Web Speech API to start listening to speech from the microphone.
// stopListening(): Causes the Web Speech API to stop listening to speech from the microphone, but will finish processing any remaining speech.
//
// source: https://www.npmjs.com/package/react-speech-recognition
export default connect(null, mapDispatchToProps)(SpeechRecognition(Speech));
